/* Message catalogues.
 *
 * `i18n.md`: no user-facing string literal in code, ever — including
 * placeholders, validation messages, `aria-label`s and page titles. Every one
 * is a key resolved here.
 *
 * ── what this is not, yet ────────────────────────────────────────────────────
 * There is **no ICU MessageFormat** in this file. `i18n.md` requires it for
 * anything with a count, a choice, or a gendered reference — and nothing in the
 * authentication screens has any of those. Adding `intl-messageformat` now
 * would be a dependency carried for a case that does not exist.
 *
 * **The first message with a count is the trigger.** At that point the
 * formatter is swapped for ICU and these strings become ICU strings; named
 * interpolation is already the syntax ICU uses, so the catalogue does not
 * change shape. What must NOT happen is an `if (count === 1)` branch appearing
 * somewhere instead — English has two plural forms, Turkish has a different
 * rule again, and Arabic has six.
 */
export const LOCALES = ['en', 'tr'];
/** The source locale, and the end of every fallback chain — ADR-0014. */
export const SOURCE_LOCALE = 'en';
/* Keys are namespaced by feature with `snake_case` leaves, and named for
 * MEANING rather than content: `auth.sign_in.submit`, never
 * `auth.sign_in.sign_in_button_text`. The copy will change; the meaning will
 * not. */
const en = {
    'app.name': 'eventuley',
    'auth.sign_up.title': 'Create your account',
    'auth.sign_up.email_label': 'Email address',
    'auth.sign_up.name_label': 'Your name',
    'auth.sign_up.password_label': 'Password',
    /* The requirement is stated BEFORE submission, not discovered after — that is
     * the accessibility criterion in STORY-001, and it is also just kinder. */
    'auth.sign_up.password_hint': 'At least 8 characters. No rules about capitals or symbols.',
    /* Says what is true today, and no more.
     *
     * It read "By continuing you accept the terms and the data processing
     * agreement" while both documents were unpublished drafts that no route
     * served and nobody could open. That is not an acceptance — and the API was
     * recording an agreement row against `unpublished-2026-08-15` to match it,
     * producing evidence of consent to a document that did not exist.
     *
     * **At publication this string is replaced** by one naming the documents,
     * with links, and the recording turns itself back on when the real versions
     * are set — `docs/legal/README.md` step 4. */
    /* Shown while TERMS_VERSION / DPA_VERSION still carry the `unpublished-`
     * sentinel. The API writes no agreement row in that state, so claiming an
     * acceptance here would be a claim the database does not support. */
    'auth.sign_up.agreement': 'Terms of service and a data processing agreement are being finalised and are not yet published. They will apply once they are, and you will be asked to accept them.',
    /* Shown once both versions are real — #71 removed a version of this line
     * that asserted acceptance of documents nobody could open.
     *
     * Three whole strings rather than one sentence with the links spliced into
     * it. `i18n.md` forbids building a sentence from translated fragments, and
     * the translator returns a string, so a link cannot be a parameter. The
     * sentence stands alone and the two links follow it as complete labels —
     * which also gives a screen reader something meaningful to announce instead
     * of the words "terms of service" torn out of the middle of a clause. */
    'auth.sign_up.agreement_published': 'By creating an account you accept the terms of service and the data processing agreement.',
    'auth.sign_up.read_terms': 'Read the terms of service',
    'auth.sign_up.read_dpa': 'Read the data processing agreement',
    /* Reading a published document — STORY-048. */
    'legal.back': 'Back to sign in',
    'legal.loading': 'Loading the document…',
    'legal.unavailable_title': 'Not available',
    'legal.unavailable_body': 'This document has not been published yet. If you were sent this link, ask whoever sent it for the current version.',
    'legal.version': 'Version {version}, in effect from {date}',
    'auth.sign_up.submit': 'Create account',
    'auth.sign_up.have_account': 'Already have an account?',
    'auth.sign_up.sign_in_link': 'Sign in',
    'auth.verify.title': 'Check your email',
    /* `{email}` is named and typed, never positional — `i18n.md`. A translator
     * moving it to a different position in the sentence must be able to. */
    'auth.verify.sent_to': 'We sent a link to {email}.',
    'auth.verify.why': 'Verifying is what unlocks creating or joining a workspace.',
    'auth.verify.verifying': 'Confirming your email address…',
    'auth.verify.verified_title': 'Email confirmed',
    'auth.verify.verified_body': 'You can create a workspace or join one now.',
    'auth.verify.continue': 'Continue',
    'auth.verify.expired_title': 'That link has expired',
    'auth.verify.expired_body': 'Sign in and we will send you another.',
    'auth.verify.invalid_title': 'That link is not valid',
    'auth.verify.invalid_body': 'Check you copied the whole address from the email.',
    'auth.verify.go_to_sign_in': 'Go to sign in',
    'auth.sign_in.title': 'Sign in',
    'auth.sign_in.email_label': 'Email address',
    'auth.sign_in.password_label': 'Password',
    'auth.sign_in.submit': 'Sign in',
    'auth.sign_in.forgot': 'Forgotten your password?',
    'auth.sign_in.no_account': 'No account yet?',
    'auth.sign_in.create_link': 'Create one',
    /* Deliberately unhelpful, and it will look like a bug to anyone who does not
     * know why. It is the same message for a wrong password and for an address
     * with no account, which is what stops the form being used to discover who
     * has one. */
    'auth.sign_in.failed': 'That email and password do not match an account.',
    'auth.sign_in.locked_title': 'Too many attempts',
    'auth.sign_in.locked_body': 'Sign-in is paused for this address for 15 minutes. If it was you, resetting your password will let you back in sooner.',
    /* ── workspaces — STORY-004 ─────────────────────────────────────────────── */
    'workspace.choose.title': 'Create a workspace, or join one',
    'workspace.choose.body': 'A workspace is your team’s events, contacts, and money. You can belong to more than one.',
    'workspace.choose.create': 'Create a workspace',
    'workspace.choose.join_code': 'Join with an invitation code',
    /* The two join routes are shown and disabled rather than hidden: the fork is
     * the point of the screen, and the reason is text so it is readable and
     * translatable rather than a tooltip nobody on a phone can reach. */
    'workspace.choose.join_code_why': 'Not built yet. An invitation code will let you join a team straight away.',
    'workspace.choose.join_id': 'Request to join by workspace ID',
    'workspace.choose.sign_out': 'Sign out of {email}',
    /* The waiting room — STORY-008. A User with no Memberships lands here on
     * every sign-in until something is approved, so this copy is a home screen's
     * rather than a confirmation's.
     *
     * Nothing here names the workspace, and that is not an oversight: the server
     * answers identically for one that does not exist, so any copy claiming
     * otherwise would make the SCREEN the enumeration oracle the API refused to
     * be. The ID is shown because the requester typed it. */
    /* Invitations addressed to this User — STORY-004.
     *
     * These DO name the workspace, unlike the join requests below, and the
     * difference is not an inconsistency: an invitation was sent to this address
     * on purpose, so the name is something the recipient has already been told by
     * email. A join request names only the id the requester typed, because the
     * server refuses to confirm whether a workspace exists.
     *
     * `{role}` is the Role the invitation grants, which is worth saying before
     * somebody accepts — joining as an Owner is a different thing to agree to. */
    'workspace.invited.heading': 'You have been invited',
    'workspace.invited.expires': 'As {role} · expires {when}',
    'workspace.invited.join': 'Join',
    'workspace.waiting.heading': 'You are not in a workspace yet',
    'workspace.waiting.status': 'Awaiting approval',
    'workspace.waiting.asked': 'Requested {when} · an Owner has to approve it',
    'workspace.waiting.withdraw': 'Withdraw the request to {id}',
    'workspace.waiting.meanwhile': 'You keep no access until then. In the meantime you can start your own.',
    /* Belonging to several teams — STORY-009. The same screen as the fork above,
     * in its other state: somebody who already holds Memberships is switching
     * rather than choosing for the first time, and a heading that says "create a
     * workspace, or join one" describes neither what they are looking at nor
     * what they came to do. */
    'workspace.switch.title': 'Your workspaces',
    'workspace.switch.body': 'One login, any number of teams.',
    'workspace.switch.another': 'Somewhere else',
    /* Shown ONLY when two workspaces share a name, which is legitimate and must
     * stay usable. The id is the thing that differs, and it is already how
     * somebody asks to join one. */
    'workspace.switch.disambiguate': 'ID {id}',
    'workspace.switch.failed': 'That workspace is not available to you any more.',
    /* Announced, not just rendered: a context switch nobody is told about is
     * disorienting, and for a screen-reader user it is invisible. */
    'workspace.switch.entered': 'Now in {name}.',
    'workspace.join.title': 'Ask to join a workspace',
    'workspace.join.body': 'Anyone in the workspace can read its ID from their address bar. Paste it here.',
    'workspace.join.id_label': 'Workspace ID',
    'workspace.join.id_hint': 'An Owner has to approve it. You get no access until they do.',
    'workspace.join.id_invalid': 'That is not a workspace ID. It looks like {example}.',
    'workspace.join.submit': 'Send the request',
    'workspace.join.submitting': 'Sending…',
    /* True whether or not the workspace exists, which is the point. */
    'workspace.join.sent': 'If that workspace exists, an Owner will see your request. Nothing happens until they approve it.',
    'workspace.join.rate_limited': 'Too many requests. Wait a while before trying another ID.',
    'workspace.join.back': 'Back',
    'workspace.create.title': 'Name your workspace',
    'workspace.create.name_label': 'Workspace name',
    'workspace.create.currency_label': 'Currency',
    'workspace.create.currency_common': 'Common',
    'workspace.create.currency_all': 'All currencies',
    'workspace.create.currency_empty': 'No currency matches that.',
    /* Deliberately worded with no grammatical agreement — "Matching currencies:
     * 1" and ": 5" are both correct, in English and in Turkish. So this carries a
     * count without needing plural rules, and the ICU trigger in this file's
     * header is NOT pulled by it. The trigger is still the first message that
     * genuinely needs agreement; an `if (count === 1)` remains forbidden. */
    'workspace.create.currency_results': 'Matching currencies: {count}',
    'workspace.create.currency_hint': 'Locked once you record money. Everything else stays editable.',
    'workspace.create.timezone_detected': 'Times will show in {timezone}, detected from this device. You can change it in settings.',
    'workspace.create.submit': 'Create workspace',
    'workspace.create.back': 'Back',
    'workspace.create.name_too_long': 'Use 60 characters or fewer.',
    'workspace.create.currency_required': 'Choose a currency.',
    'workspace.create.currency_unknown': 'That is not a currency code we recognise.',
    /* ── navigation — STORY-039 ─────────────────────────────────────────────────
     * Eight destinations, and the list is identical on both surfaces: only its
     * presentation differs. Two different sets would be two products. */
    'nav.label': 'Main',
    'nav.open': 'Open the menu',
    'nav.close': 'Close the menu',
    'nav.calendar': 'Calendar',
    'nav.events': 'Events',
    'nav.venues': 'Venues',
    'nav.people': 'People',
    'nav.tasks': 'Tasks',
    'nav.money': 'Money',
    'nav.inventory': 'Inventory',
    'nav.settings': 'Settings',
    'nav.account': 'Account',
    'nav.switch_workspace': 'Switch workspace',
    'nav.sign_out': 'Sign out',
    /* Named for meaning: this is the Role on THIS Membership, and the same User
     * may hold a different one in the next workspace — STORY-006. */
    'role.owner': 'Owner',
    'role.member': 'Member',
    'nav.not_built_title': '{destination} is not built yet',
    'nav.not_built_body': 'The destination is listed because it exists in the product. Its screen arrives in a later story.',
    /* ── settings — STORY-006 ───────────────────────────────────────────────── */
    'settings.title': 'Settings',
    'settings.general': 'Workspace',
    'settings.name_label': 'Workspace name',
    'settings.rename': 'Save the name',
    'settings.renamed': 'The workspace name is saved.',
    /* A Member sees the name and cannot change it. The reason is TEXT, never a
     * greyed control with no explanation — STORY-006 and `design.md`. */
    'settings.owner_only': 'Only an Owner can change this.',
    /* How long inactive People are kept — STORY-043.
     *
     * The copy has one job beyond naming the control: making clear that this
     * DESTROYS data and cannot be undone. `data-map.md` makes the period the
     * customer's decision because they are the controller of their guests' data —
     * so the screen has to give them enough to decide with. */
    'settings.retention.heading': 'Erasing inactive guests',
    'settings.retention.explanation': 'You decide how long to keep a guest who has stopped attending. This is off unless you turn it on, and nothing is erased until you do.',
    'settings.retention.label': 'Erase a guest after',
    'settings.retention.off': 'Never — keep everyone',
    /* ICU plural. `i18n.md` forbids an `if (n === 1)` branch, and Turkish has one
     * form after a number where English has two — which is why the Turkish entry
     * below has only `other`. */
    'settings.retention.months': '{count, plural, one {# month of no activity} other {# months of no activity}}',
    /* Over the plan's ceiling — STORY-045.
     *
     * ICU plural on the count, and a full sentence per key rather than fragments:
     * Turkish attaches its suffixes to the word, so a sentence assembled in
     * English word order cannot be reassembled. The meter name is a parameter
     * because the three names decline identically here and the sentence does not
     * change shape between them. */
    'over_limit.title': 'This workspace is over its plan',
    'over_limit.meter': '{meter}: {used} of {limit}. {count, plural, one {Remove # to fit again} other {Remove # to fit again}}.',
    'over_limit.events': 'Active events',
    'over_limit.seats': 'Members',
    'over_limit.contacts': 'Contacts',
    /* Both ways out, always. A banner that offers only removal reads as a threat,
     * and paying is the other exit — the story says every lock lifts at once when
     * an Owner pays. */
    /* Shown only after a write has actually been refused, so it is news rather
     * than a permanent scold. Says the change was NOT saved, because the failure
     * it describes was previously silent and looked like nothing happening. */
    'over_limit.refused': 'That change was not saved — this workspace is read-only until it fits.',
    /* On the disabled control itself. A control that cannot work must say so
     * before it is pressed, not after — that gap is what made the whole feature
     * read as broken. */
    'over_limit.control_disabled': 'This workspace is over its plan and is read-only until it fits.',
    'over_limit.exits': 'Nothing has been deleted. You can remove what you no longer need, or move to a larger plan.',
    'settings.retention.warning': 'This permanently erases the name, phone number, email and notes of guests with no activity in that time. Their attendance records and your headcounts are kept. It cannot be undone.',
    'settings.retention.save': 'Save',
    'settings.retention.saved': 'Saved. The next sweep will use this.',
    /* Its own wording rather than reusing `settings.owner_only`. Two identical
     * sentences on one screen read as a rendering bug, and a Member seeing this
     * section needs to know which setting is out of their hands, not merely that
     * something is. */
    'settings.retention.owner_only': 'Only an Owner can change how long guests are kept.',
    'settings.members': 'Members',
    'settings.members_body': 'Who is in this workspace, and what each of them can do.',
    'settings.role_label': 'Role for {name}',
    'settings.last_owner': 'A workspace always keeps at least one Owner. Promote somebody else first.',
    'settings.role_changed': '{name} is now {role}.',
    'settings.owner_explains': 'Owners control who is in the team, what it pays for, and how the workspace is set up. Everything else — events, people, money, tasks and equipment — every Member can do.',
    /* Leaving — STORY-009. Available to every Role: what stops an Owner is the
     * last-Owner guard, not their Role. The copy leads with what SURVIVES,
     * because the fear on this button is losing your own work. */
    'settings.leave.heading': 'Leave this workspace',
    'settings.leave.body': 'Your access ends. Everything you recorded stays with the workspace, still attributed to you, and your other workspaces are untouched.',
    'settings.leave.submit': 'Leave this workspace',
    'settings.leave.confirm': 'Leave {name}?',
    'settings.leave.explains': 'You will need a new invitation to come back.',
    'settings.leave.yes': 'Leave',
    'settings.leave.cancel': 'Stay',
    /* Names the remedy that exists — for somebody who HAS colleagues. */
    'settings.leave.last_owner': 'You are this workspace’s only Owner. Make somebody else an Owner first.',
    /* ── the enabled set — STORY-041 ─────────────────────────────────────────── */
    /* Two sets, one component, and the copy is written out per kind rather than
     * assembled from a noun and a sentence. `i18n.md` forbids building one string
     * from fragments: Turkish attaches suffixes to the word, so a sentence in
     * English word order cannot be reassembled from the same pieces. More keys,
     * and each one translatable on its own. */
    'settings.currencies.heading': 'Currencies',
    'settings.currencies.search_label': 'Search all currencies',
    'settings.currencies.search_placeholder': 'Code or name',
    'settings.currencies.hint': 'Your team can only choose from the currencies listed here. Search above to add the ones you work in — every currency in the world is available.',
    'settings.currencies.empty': 'No currency matches “{query}”. Try a code like JPY, or a name like Yen.',
    /* Worded to need no plural agreement, like every other count-carrying message
     * until ICU MessageFormat arrives at STORY-016. An `if (count === 1)` branch
     * is forbidden — English has two plural forms and Turkish has a different
     * rule again. */
    'settings.currencies.count_enabled': 'Currencies enabled: {count}',
    'settings.currencies.count_matches': 'Currencies matching “{query}”: {count}',
    'settings.currencies.own': 'Workspace currency',
    'settings.currencies.own_locked': 'The workspace currency. Change it in Workspace above.',
    'settings.currencies.last_locked': 'The last currency. A workspace always keeps at least one.',
    'settings.currencies.member_note': 'You can use every currency listed here and read every figure in it. Adding a new one is an Owner’s call — ask one, and it appears here.',
    'settings.timezones.heading': 'Timezones',
    'settings.timezones.search_label': 'Search all timezones',
    'settings.timezones.search_placeholder': 'Zone, city, or offset',
    'settings.timezones.hint': 'Your team can only choose from the timezones listed here. Search above to add the ones you work in — every IANA zone is available.',
    'settings.timezones.empty': 'No timezone matches “{query}”. Try a city like Tokyo, or a zone like Asia/Tokyo.',
    'settings.timezones.count_enabled': 'Timezones enabled: {count}',
    'settings.timezones.count_matches': 'Timezones matching “{query}”: {count}',
    'settings.timezones.own': 'Workspace timezone',
    'settings.timezones.own_locked': 'The workspace timezone. Change it in Workspace above.',
    'settings.timezones.last_locked': 'The last timezone. A workspace always keeps at least one.',
    'settings.timezones.member_note': 'You can use every timezone listed here. Adding a new one is an Owner’s call — ask one, and it appears here.',
    'settings.enabled.group_on': 'Enabled',
    'settings.enabled.group_off': 'Not enabled',
    /* Every control's accessible name carries its SUBJECT. Twelve controls all
     * named "Add" is the usual failure in a list of commands. */
    'settings.enabled.add': 'Add {name}',
    'settings.enabled.toggle': 'Stop offering {name}',
    'settings.enabled.locked_label': '{name} is offered and cannot be turned off',
    'settings.enabled.on': 'Enabled',
    'settings.enabled.always_on': 'Always on',
    'settings.enabled.owner_only': 'Owner only',
    'settings.enabled.unused': 'Not used yet',
    'settings.enabled.used': 'Records using it: {count}',
    /* Shown only when the count is not 2 — the point is that it comes from the
     * currency rather than from an assumption, so the exceptions are what a
     * reader needs to see. */
    'settings.enabled.decimals_none': 'No decimal places',
    'settings.enabled.decimals': 'Decimal places: {count}',
    'settings.enabled.confirm_title': 'Stop offering {name}?',
    'settings.enabled.confirm_body': 'Records that already use it keep it — you just won’t be offered it on new ones. You can turn it back on at any time.',
    'settings.enabled.confirm_yes': 'Turn it off',
    'settings.enabled.confirm_no': 'Keep it',
    'settings.enabled.failed': 'That change did not save. The list still shows what the server has.',
    /* An unreadable set is not an empty one. Without this the screen would say
     * 'enabled: 0' after a failed read, which is a sentence that is not true. */
    'settings.enabled.unreadable': 'We could not read what this workspace offers. Reload to try again.',
    'settings.enabled.more': 'And more, not shown: {count}. Keep typing to narrow it.',
    /* ── the account — STORY-010 ─────────────────────────────────────────────── */
    /* A launch blocker rather than a backlog item: a product taking signups
     * worldwide cannot ship without a working access and erasure path.
     *
     * Every one of these has to be unambiguous in translation. A mistranslated
     * deletion warning destroys data, which is why the consequences are spelled
     * out in full sentences rather than assembled from a verb and a noun. */
    'account.export.heading': 'Download your data',
    'account.export.body': 'Everything we hold about you as a person: your account, what you agreed to, and every workspace you belong to.',
    /* Said BEFORE it is asked for rather than discovered afterwards. Somebody
     * asking for "everything about me" reasonably expects the workspace's guests
     * and money too, and those are the customer's data — that request goes to
     * them, not to us. Cheaper than answering the support email. */
    'account.export.excludes': 'It does not include a workspace’s events, guests or money. Those belong to the workspace, and its Owner is who to ask for them.',
    'account.export.submit': 'Download my data',
    'account.export.ready': 'Here it is',
    'account.export.rate_limited': 'That is a lot of downloads. Try again later.',
    'account.close.heading': 'Close your account',
    'account.close.body': 'Your access ends everywhere. What you recorded stays with each workspace, with your name gone from it.',
    'account.close.sole_owner': 'You are the only Owner of {name}',
    'account.close.sole_owner_body': 'Decide what happens to it. A workspace is never left without an Owner, and it is never closed as a side effect of somebody leaving.',
    'account.close.what_happens': 'What happens to {name}',
    'account.close.choice_close': 'Close {name} too, with everything in it',
    'account.close.choice_promote': 'Hand it over — I will make somebody else an Owner first',
    /* The subscription line is a criterion rather than decoration: a closed
     * workspace still billing a deleted account is the failure the story names. */
    'account.close.close_warning': 'Its events, guests, money and equipment go with it. People who lose access: {count}. Any paid subscription is cancelled as part of this.',
    'account.close.open_members': 'Open Members and promote somebody',
    'account.close.password_label': 'Your password',
    'account.close.wrong_password': 'That password does not match.',
    'account.close.undecided': 'Answer the question about every workspace you are the only Owner of.',
    /* Names which answer is missing rather than greying a button and leaving the
     * reader to work out why — STORY-010's accessibility criterion. */
    'account.close.blocked': 'Answer the question above about {names} before this can go ahead.',
    /* The number comes from the server, because the grace is an operator setting
     * and this is the figure somebody is relying on. Hardcoding 30 would tell
     * them the wrong one the first time it changed. */
    'account.close.grace': 'Nothing is destroyed for {days} days. Signing in before then cancels it.',
    'account.close.submit': 'Close my account',
    'account.closed.title': 'Your account closes on {when}',
    'account.closed.body': 'Until then nothing is destroyed and signing in brings everything back. After it, what remains is anonymous: the records other people depend on, with your name gone from them.',
    'account.closed.undo': 'Keep my account',
    /* ── events — STORY-011 ──────────────────────────────────────────────────── */
    'events.title': 'Events',
    'events.new': 'New event',
    'events.no_date': 'No date yet',
    /* ── the list, filtered and sorted — STORY-040 ─────────────────────────────── */
    /* The one control most people will ever touch. The default already hides
     * these, so this turns them ON rather than off — "hide inactive" is not a
     * feature when it is what you already see. */
    'events.filters.show_finished': 'Show completed and cancelled',
    'events.filters.more': 'Filters and sorting',
    'events.filters.status_legend': 'Status',
    'events.filters.venue': 'Venue',
    'events.filters.any_venue': 'Any venue',
    'events.filters.organiser': 'Organiser',
    'events.filters.any_organiser': 'Anyone',
    'events.filters.when': 'When',
    'events.filters.any_time': 'Any time',
    'events.filters.last_7': 'The last 7 days',
    'events.filters.last_30': 'The last 30 days',
    'events.filters.last_90': 'The last 90 days',
    'events.filters.last_365': 'The last year',
    'events.filters.sort': 'Order by',
    'events.filters.sort_date': 'Date',
    'events.filters.sort_name': 'Name',
    'events.filters.sort_status': 'Status',
    /* The direction says which way in words. An arrow alone carries its meaning
     * in shape and colour only, which `design.md` refuses — and the words
     * depend on what is being sorted, because "earliest first" describes nothing
     * about an alphabetical order. */
    'events.filters.date_asc': 'Earliest first',
    'events.filters.date_desc': 'Latest first',
    'events.filters.name_asc': 'A to Z',
    'events.filters.name_desc': 'Z to A',
    /* The lifecycle order, so the ends of it are the words — never "ascending",
     * which describes the implementation rather than the list. */
    'events.filters.status_asc': 'Ideas first',
    'events.filters.status_desc': 'Cancelled first',
    /* Each active filter is named and individually removable, plus one clear-all
     * — so a link somebody was sent can always be undone. */
    'events.filters.active': 'Filtering by:',
    'events.filters.remove': 'Remove the {filter} filter',
    'events.filters.clear': 'Clear all filters',
    'events.filters.chip_status': 'Status',
    'events.filters.chip_venue': 'Venue',
    'events.filters.chip_organiser': 'Organiser',
    'events.filters.chip_when': 'When',
    /* The distinction that matters most on this screen: "you have no events" and
     * "you have events but none match" are different statements, and confusing
     * the two is alarming. */
    'events.filters.none_match.title': 'Nothing matches those filters',
    'events.filters.none_match.body': 'There are events here — just none that fit what you have asked for.',
    /* A RANGE, not a running total. The list shows one page at a time, and
     * "showing 26 of 40" above a single row is a sentence that contradicts the
     * screen it sits on. */
    'events.list.showing': 'Showing {from}–{to} of {total}',
    'events.list.previous': 'Previous page',
    'events.list.next': 'Next page',
    'events.list.venue_unknown': 'No venue yet',
    /* The empty workspace is the highest-churn screen in the product, so this
     * copy is a decision rather than a detail: ONE line of orientation — why
     * Events rather than People or Tasks — and then a single action. */
    'events.empty.title': 'Nothing here yet',
    'events.empty.body': 'Events are the centre of it all — guests, tasks and money all hang off one. Start with just a name.',
    'events.empty.action': 'Add your first event',
    'events.capture.heading': 'A new event',
    'events.capture.name_label': 'What is it called?',
    /* Said out loud, because the whole story is that a name is enough. */
    'events.capture.rest_optional': 'Everything else can wait. You can add it whenever you know it.',
    'events.capture.date_label': 'Date',
    'events.capture.time_label': 'Start time',
    'events.capture.time_needs_date': 'A time needs a date. Add one, or clear the time.',
    'events.capture.name_required': 'Give it a name — anything you will recognise.',
    'events.capture.submit': 'Save it',
    'events.capture.cancel': 'Not now',
    'events.capture.gone': 'This workspace is not available to you any more.',
    'events.status.idea': 'Idea',
    'events.status.planned': 'Planned',
    'events.status.confirmed': 'Confirmed',
    'events.status.completed': 'Completed',
    'events.status.cancelled': 'Cancelled',
    /* ── the lifecycle — STORY-012 ────────────────────────────────────────────── */
    'events.lifecycle.heading': 'Where it stands',
    'events.lifecycle.label': 'Status',
    'events.lifecycle.save': 'Change it',
    /* Prompted, never blocked. The wording is deliberately not a warning: the
     * move is going to succeed, and this is useful information rather than an
     * objection to it. */
    'events.lifecycle.incomplete': 'Worth knowing before you commit:',
    'events.lifecycle.missing_date': 'No date yet.',
    'events.lifecycle.missing_venue': 'No venue yet.',
    'events.lifecycle.early_completion': 'This has not happened yet. Settling it early is fine — the date stays as it is.',
    /* The one refusal in the story, and it is billing rather than lifecycle. It
     * names the plan that LIFTS the limit, not the one the workspace is on. */
    /* "still an idea" was wrong on two of the three paths that reach here: the
     * refusal also fires reopening a completed Event and reviving a cancelled
     * one. What is true every time is that the Event did not move. */
    'events.lifecycle.no_active_events': 'Your plan has no active events left. The {plan} plan raises the limit. Nothing was lost — this event has not moved.',
    /* The status it has NOW comes from the server in the error fields, so this
     * sentence is built here rather than parsed from a message. */
    'events.lifecycle.moved_underneath': 'Somebody else moved this first. It is now {status}. Have another look before changing it.',
    'events.lifecycle.history_heading': 'What has happened to it',
    'events.lifecycle.history_line': '{from} → {to}',
    'events.lifecycle.history_by': '{who} · {when}',
    /* An account that has been erased. ADR-0015 keeps the record and severs the
     * subject, so the move still happened and still had somebody behind it — the
     * name is simply gone. One fixed label from here, never a string stored on
     * the server. */
    'events.person_removed': 'Someone who has left',
    'events.detail.back': 'Back to events',
    'events.detail.missing': 'That event is not here',
    'events.detail.save': 'Save',
    'events.detail.saved': 'Saved.',
    'events.detail.name_heading': 'Name',
    'events.detail.when_heading': 'When',
    /* An Event at Idea has no Venue and therefore no Venue timezone, so a time
     * entered now resolves against the workspace's — EPIC-002 risk 2. Said on the
     * screen rather than left to be discovered when the Venue arrives. */
    'events.detail.when_hint': 'Until this event has a venue, its time is read in the workspace’s timezone.',
    'events.detail.budget_heading': 'Budget',
    'events.detail.budget_hint': 'What you expect it to cost and to bring in. Both are optional, and each keeps its own currency.',
    'events.detail.budget_label': 'Planned budget',
    'events.detail.budget_currency_label': 'Budget currency',
    'events.detail.revenue_label': 'Expected revenue',
    'events.detail.revenue_currency_label': 'Revenue currency',
    /* A figure whose currency was later disabled stays listed and stays selected
     * — enablement governs the picker, never the stored record. STORY-041. */
    'events.detail.currency_disabled': '{code} — no longer offered',
    'events.detail.notes_heading': 'Notes',
    'events.detail.notes_label': 'Anything worth remembering',
    /* The zone an Event's times are read in — resolved from its Venue, or the
     * workspace's when it has none. Shown rather than assumed: "19:30" means
     * nothing on its own. */
    'events.detail.reads_in': 'Times here are read in {zone}.',
    /* ── venues — STORY-013 ──────────────────────────────────────────────────── */
    /* ── the contact book — STORY-015 ──────────────────────────────────────────── */
    /* ── the guest list — STORY-016 ────────────────────────────────────────────── */
    'guests.heading': 'Who is coming',
    /* THE message ICU was adopted for. Two counts, two plurals, one sentence —
     * and an `if (n === 1)` would be wrong in both directions at once: English
     * has two forms and Turkish takes one after a number. */
    'guests.totals': '{guests, plural, =0 {Nobody invited yet} one {# guest} other {# guests}}{guests, plural, =0 {} other {, {pax, plural, one {# person} other {# people}}}}',
    'guests.add_label': 'Add somebody',
    'guests.add_hint': 'Search the contact book, or type a name that is not in it yet.',
    'guests.no_match': 'Nobody in the contact book matches that.',
    'guests.results': '{count, plural, one {# match} other {# matches}}',
    'guests.create': 'Add “{name}” as a new person',
    'guests.gone': 'That person is no longer here. Try searching again.',
    /* The contact cap reaches the guest list too, because adding somebody new
     * from here creates a contact. Said as what it is, rather than reading as a
     * failure to add a guest. */
    'guests.contact_cap': 'Your plan is at its contact limit, so this person could not be saved. Someone already in the contact book can still be added.',
    /* A refused change to a headcount has to be visible: the field would
     * otherwise keep showing a number nobody stored. */
    'guests.pax_failed': 'That headcount was not saved. The number shown is the one on record.',
    'guests.remove_failed': 'They could not be taken off the list. Nothing has changed.',
    'guests.empty': 'Nobody is on this list yet.',
    /* A DIFFERENT fact from an empty list, and the story calls out the reference
     * workbook for being unable to tell them apart: it returned zero either way,
     * so "nobody has replied" and "nobody was asked" looked identical. */
    'guests.none_confirmed': 'Nobody has confirmed yet, so the headcount is zero. Everyone invited is still on the list.',
    /* Said out loud rather than silently doing nothing. The picker can only
     * exclude the guests on the page it can see, so somebody invited on another
     * page is still offered — and an add that changes nothing on screen is
     * indistinguishable from one that failed. */
    'guests.already_invited': '{name} is already on this list, on another page.',
    /* ── the row's controls carry two names ───────────────────────────────────
     * The visible label is short because it sits in a dense row, and a row is
     * read in context: the guest's name is right beside it. The ACCESSIBLE name
     * is the long one below, because a screen reader user arrowing through forty
     * selects hears "Status, Status, Status" otherwise and has to leave the
     * control to find out whose it is.
     *
     * This is also what makes the row a row. The long labels were the visible
     * ones, and their width is what stopped `.row__actions` ever fitting beside
     * the title — so it wrapped to its own line, and then each field wrapped to
     * a line of its own inside it. Five guests came to 1,800px of screen. */
    'guests.status_short': 'Status',
    'guests.payment_short': 'Payment',
    'guests.pax_short': 'Pax',
    'guests.status_label': 'Where {name} stands',
    'guests.page_size': 'Guests per page',
    'guests.page_size_n': '{count, plural, other {# at a time}}',
    'guests.page_size_all': 'All of them',
    'guests.showing': 'Showing {from}–{to} of {total, plural, one {# guest} other {# guests}} · page {page} of {pages}',
    'guests.previous': 'Previous page',
    'guests.next': 'Next page',
    /* Names the guest, because a list of them all reads "Pax, Pax, Pax" to a
     * screen reader otherwise. */
    'guests.pax_label': 'How many is {name} bringing, including themselves',
    'guests.pax_invalid': 'A whole number of people, starting at one.',
    /* Warns, never blocks — no number is definitely wrong, and a mistyped one
     * silently inflates every headcount downstream. */
    'guests.pax_implausible': 'That is {pax, plural, one {# person} other {# people}} on one invitation. Worth a second look, but it is saved.',
    'guests.remove': 'Remove',
    'guests.status.invited': 'Invited',
    'guests.status.confirmed': 'Confirmed',
    'guests.status.attended': 'Attended',
    'guests.status.cancelled': 'Cancelled',
    /* Translator note: "Comped" is hospitality jargon — admitted without
     * charge, as a hospitality decision rather than a discount. Many languages
     * have no single word for it; describe the concept rather than the word.
     * "Free of charge" or "guest of the house" is closer than "discounted". */
    'guests.payment.unpaid': 'Unpaid',
    'guests.payment.paid': 'Paid',
    'guests.payment.comped': 'Comped',
    'guests.payment.refunded': 'Refunded',
    /* Names the guest, as the status label does: forty rows of "Payment" read
     * "Payment, Payment, Payment" to a screen reader otherwise. */
    'guests.payment_label': 'Whether {name} has paid',
    'guests.payment_failed': 'That payment status was not saved. What is shown is what is stored.',
    /* One key, never four fragments joined in code — the separator and the word
     * order are the translator's to change. All four are always shown, zeroes
     * included: a strip that hides "refunded" until somebody is refunded changes
     * shape under the reader, and a zero is an answer to "who still owes me". */
    'guests.payments': '{paid} paid · {unpaid} unpaid · {comped} comped · {refunded} refunded',
    /* ── EPIC-004: money ──────────────────────────────────────────────────────
     * The amount field is the highest-risk control in the product, and the risk
     * is not the styling. Every message here is about a number being read the
     * way it was meant. */
    'money.title': 'Money',
    'money.record': 'Record money',
    'money.record_heading': 'What moved',
    'money.record_submit': 'Record it',
    'money.record_cancel': 'Not now',
    'money.direction.income': 'Money in',
    'money.direction.expense': 'Money out',
    'money.direction_label': 'In or out?',
    'money.amount_label': 'How much',
    /* Deliberately vague about the shape. The separators come from the reader's
     * own locale — 1.234,50 is one thousand in Turkish and one point two in
     * English — so naming a format here would be right for one of them and wrong
     * for the other. */
    'money.amount_invalid': 'That is not an amount. Type it the way you would say it.',
    /* Two refusals, kept apart on purpose. A negative is a contradiction, since
     * "in or out" already carries the sign; a zero is a placeholder somebody
     * meant to come back to rather than a fact about money that moved. */
    'money.amount_above_zero': 'An amount above zero. Whether it went out is set by “in or out”, not by a minus sign.',
    'money.currency_label': 'Currency',
    /* Says where the full set lives rather than offering an inline enable this
     * form would have to honour — i18n.md keeps searching all ~180 in one place. */
    'money.currency_hint': 'An Owner can add more in Settings → Currencies.',
    'money.currency_not_enabled': 'That currency is not one this workspace uses yet. An Owner can add it in Settings.',
    'money.date_label': 'When',
    /* The day, in the workspace's own timezone. Recording just after midnight
     * must not file it against yesterday, which is what storing an instant and
     * rendering it back would do for anybody east of UTC. */
    'money.date_hint': 'The day it happened, where you are.',
    'money.category_label': 'What for',
    'money.category_none': 'Choose one',
    /* The general refusal, for a field the form has no wording of its own for.
     * Vague on purpose — the server names the field and the client should not
     * guess at a translation for a rule it has not been taught. */
    'money.check_the_form': 'Something on this form was not accepted. Check the fields above.',
    'money.category_wrong_direction': 'That category belongs to the other side. Categories are kept apart so an “in” total can never quietly include an “out”.',
    'money.event_label': 'Which event',
    /* Named, never a blank option. A Transaction belonging to no Event is a real
     * and expected answer — an empty entry reads as one somebody forgot. */
    'money.event_overhead': 'Overhead — not one event',
    'money.event_hint': 'Leave it on overhead for anything that is not one night’s cost.',
    'money.event_hint_truncated': 'Showing the {count} most recent events. Older ones are not offered here yet.',
    'money.description_label': 'What was it',
    'money.counterparty_label': 'Who (optional)',
    'money.counterparty_hint': 'A supplier, or whoever was paid.',
    'money.totals_label': 'Totals',
    /* One line per currency. Money in two currencies does not add up, and a
     * single figure would either be a lie or need a rate this product has not
     * got. */
    /* One key, one sentence. The row and the totals strip both use these —
     * gluing a translated word to a formatted number is exactly the
     * concatenation `i18n.md` forbids, and Turkish suffixes attach to the word. */
    'money.amount_in': 'In {amount}',
    'money.amount_out': 'Out {amount}',
    'money.row_meta': '{date} · {category}',
    'money.delete': 'Delete',
    'money.delete_failed': 'That entry was not deleted. Nothing changed.',
    'money.delete_confirm_title': 'Delete “{description}”?',
    'money.delete_confirm_body': 'The entry goes for good and its amount cannot be recovered. Only who deleted it, and when, is kept.',
    'money.delete_confirm_submit': 'Delete for good',
    'money.delete_confirm_cancel': 'Keep it',
    /* STORY-023. "Attributed" has no clean one-word equivalent in many
       languages — the note the story asks for: it means profit from money that
       names a particular event, as opposed to money that names none. */
    'reconciliation.heading': 'How the totals add up',
    'reconciliation.intro': 'Per-event profit counts only money filed against an event. Business profit counts everything. The difference is overhead, and it is shown here rather than left to be discovered.',
    'reconciliation.nothing_yet': 'Nothing has been recorded yet, so all three read zero.',
    'reconciliation.unavailable': 'These figures did not add up, so none are shown. Nothing is wrong with your records — a query is at fault and it has been reported.',
    'reconciliation.table_caption': 'Profit from money filed against events, profit from money filed against none, and the two together — one row per currency.',
    'reconciliation.currency_column': 'Currency',
    'reconciliation.attributed_column': 'From events',
    'reconciliation.overhead_column': 'Overhead',
    'reconciliation.organisation_column': 'Whole business',
    'reconciliation.adds_up_cost': 'In {currency}: {attributed} from events, less {overhead} of overhead, makes {organisation} for the whole business.',
    'reconciliation.adds_up_income': 'In {currency}: {attributed} from events, plus {overhead} that belongs to no event, makes {organisation} for the whole business.',
    'reconciliation.down': '{amount} down',
    'reconciliation.show_overhead': 'Show what {currency} overhead is made of',
    'reconciliation.hide_overhead': 'Hide what {currency} overhead is made of',
    'reconciliation.no_overhead': 'Nothing is filed as overhead.',
    'reconciliation.overhead_truncated': 'Showing {count} of {total} overhead entries.',
    /* Both ends of the page and where it sits, because "showing 25 of 40" says
     * nothing about which 25 or how to reach the rest. */
    'money.showing_page': 'Showing {from}–{to} of {total, plural, one {# entry} other {# entries}} · page {page} of {pages}',
    'money.previous': 'Previous page',
    'money.next': 'Next page',
    'money.empty.title': 'Nothing recorded yet',
    'money.empty.body': 'Every amount in and out, with what it was for — so the totals are built from real entries rather than remembered.',
    /* ── STORY-021: the categories money is filed under ───────────────────────
     * A Category is a record rather than free text, because free text fragments:
     * the same idea typed three ways becomes three categories and every total
     * built on them is wrong. */
    'money.tab_ledger': 'Entries',
    'money.tab_reconciliation': 'How it adds up',
    'money.tab_dashboard': 'At a glance',
    /* Tasks — STORY-025 and STORY-026. Translator note: "overdue" is a label on
       a row and is read alongside a due date; keep it short. The hidden-overdue
       line is the one that matters most — it is the screen admitting what its
       own default is not showing, and it must not read as an error. */
    /* The calendar — STORY-014. Translator note: month and weekday names are
       LOCALE DATA and are not here; nothing in this catalogue names a month.
       `day_label` is what a screen reader announces for a cell, so it must
       carry the date and the count and read as a sentence. */
    /* Inventory — STORY-028, 029, 030. Translator note: the six condition names
       are a fixed vocabulary from the glossary; "lightly damaged" is a real step
       between "fair" and "problematic" and should not be collapsed into either.
       `outstanding_count` is the number the whole module exists to report. */
    /* The language control — STORY-042. Translator note: these four ARE
       translated, but the language NAMES in the control are not — they are
       endonyms ("Türkçe", never "Turkish"), because the one person guaranteed to
       need this control is the one who cannot read the current language. */
    'language.label': 'Language',
    'language.heading': 'Language',
    'language.hint': 'This is yours, not a workspace’s — it follows you into every team you are in, on every device.',
    'language.not_saved': 'The screen is in the language you chose, but we could not save it to your account. It will not follow you to another device until it saves.',
    'inventory.title': 'Equipment',
    'inventory.tabs_label': 'Equipment views',
    'inventory.tab_outstanding': 'Still out',
    'inventory.tab_items': 'What we own',
    'inventory.outstanding_count': '{count, plural, =0 {Nothing is out.} one {# thing has not come back.} other {# things have not come back.}}',
    'inventory.nothing_out': 'Everything is accounted for.',
    'inventory.out_for': 'Went to {event} on {date} — {days, plural, one {# day ago} other {# days ago}}',
    'inventory.out_for_undated': 'Went to {event}, which has no date yet',
    'inventory.mark_returned': 'It came back',
    'inventory.just_returned': 'Back just now',
    'inventory.packing_heading': 'Packed for later',
    'inventory.packing_hint': 'These are for events that have not happened yet. They are not missing, so they are not counted above.',
    'inventory.packing_for': 'For {event}',
    'inventory.return.followup': 'It is marked back. What state is it in?',
    'inventory.return.skip': 'Leave it as it was',
    'inventory.condition_label': 'Condition',
    'inventory.condition_unchanged': 'Leave it as it was',
    'inventory.condition.excellent': 'Excellent',
    'inventory.condition.good': 'Good',
    'inventory.condition.fair': 'Fair',
    'inventory.condition.lightly_damaged': 'Lightly damaged',
    'inventory.condition.problematic': 'Problematic',
    'inventory.condition.lost': 'Lost',
    'inventory.add': 'Add something',
    'inventory.close_form': 'Close',
    'inventory.submit': 'Save it',
    'inventory.cancel': 'Cancel',
    'inventory.refused': 'That was not saved. Check the name and the cost.',
    'inventory.nothing_yet': 'Nothing is registered yet.',
    'inventory.name_label': 'What is it',
    'inventory.category_label': 'Category',
    'inventory.owner_label': 'Who owns it',
    'inventory.holder_label': 'Who has it',
    'inventory.nobody': 'Nobody in particular',
    'inventory.row_meta': '{condition}',
    'inventory.cost': 'Cost {amount}',
    'inventory.cost_from_books': 'Cost {amount}, from the expense that bought it',
    'inventory.held_by': 'Held by {name}',
    'inventory.former_member': 'A former member',
    'inventory.delete': 'Delete',
    'inventory.delete_in_use': 'This has been taken to an event, so the record is kept. Mark it lost instead — that keeps its cost in the history and it can be found again.',
    'inventory.delete_confirm_title': 'Delete “{name}”?',
    'inventory.delete_confirm_body': 'The record goes for good, and its cost goes with it.',
    'inventory.delete_confirm_submit': 'Delete for good',
    'inventory.delete_confirm_cancel': 'Keep it',
    'inventory.save': 'Save the changes',
    'inventory.edit': 'Change it',
    'inventory.notes_label': 'Notes',
    'inventory.purchased_label': 'Bought on',
    'inventory.cost_label': 'What it cost',
    'inventory.currency_label': 'Currency',
    'inventory.cost_invalid': 'That is not an amount.',
    'inventory.expense_label': 'The expense that bought it',
    'inventory.expense_none': 'Nothing — a gift, or before our records',
    'inventory.expense_hint': 'Linking one keeps the cost in a single place. Expenses already linked to something else are not listed.',
    'inventory.expense_linked_hint': 'The cost and the date come from that expense and follow it when it is corrected. Remove the link to enter your own.',
    'inventory.loadout_heading': 'Equipment',
    'inventory.loadout_empty': 'Nothing is going to this event yet.',
    'inventory.loadout_add': 'Take something',
    'inventory.loadout_return_all': 'It all came back',
    'inventory.loadout_returned': 'Back on {date}',
    'inventory.loadout_remove': 'Take it off the list',
    'inventory.loadout_choose': 'Choose what is going',
    'inventory.loadout_submit': 'Add to the loadout',
    'calendar.title': 'Calendar',
    'calendar.previous': 'Previous month',
    'calendar.next': 'Next month',
    'calendar.today': 'This month',
    'calendar.day_label': '{date} — {count, plural, =0 {nothing on} one {# thing on} other {# things on}}',
    'calendar.nothing_this_month': 'Nothing is planned or due this month.',
    'calendar.nothing_that_day': 'Nothing is on that day.',
    'calendar.show_whole_month': 'Show the whole month',
    'calendar.agenda_month': 'This month',
    'calendar.agenda_day': '{date}',
    'calendar.event_on': 'Event · {date}',
    'calendar.event_undated': 'Event · no date yet',
    'calendar.task_due': 'Task due {date} · {priority}',
    'calendar.task_undated': 'Task · no date yet · {priority}',
    'calendar.undated_heading': 'No date yet',
    'calendar.undated_hint': 'Ideas without a date, and unfinished work without one. They are here so they do not disappear.',
    'tasks.title': 'Tasks',
    'tasks.new': 'Write one down',
    'tasks.close_form': 'Close',
    'tasks.submit': 'Save the task',
    'tasks.cancel': 'Cancel',
    'tasks.refused': 'That task was not saved. Check the dates and who it is for.',
    'tasks.none_yet': 'Nothing is written down yet.',
    'tasks.none_match': 'No tasks match the filters you have on.',
    'tasks.clear_filters': 'Clear the filters',
    'tasks.hidden_overdue': '{count, plural, one {# overdue task is hidden by these filters.} other {# overdue tasks are hidden by these filters.}}',
    'tasks.show_hidden': 'Show them',
    'tasks.overdue': 'Overdue',
    'tasks.due': 'Due {date}',
    'tasks.row_meta': '{assignee} · {priority}',
    'tasks.assignee_erased': 'A former member',
    'tasks.assignee_left': 'This person has left the workspace. The work has not.',
    'tasks.changed_underneath': 'Somebody else changed this task while you were looking at it. Nothing was overwritten — open it again to see where it got to.',
    'tasks.filters_label': 'Filter the tasks',
    'tasks.assignee_filter_label': 'Whose',
    'tasks.assignee_mine': 'Mine',
    'tasks.assignee_everyone': 'Everyone’s',
    'tasks.status_filter_label': 'Status',
    'tasks.priority_filter_label': 'Priority',
    'tasks.status_label': 'Status',
    'tasks.description_label': 'What needs doing',
    'tasks.assignee_label': 'Who is doing it',
    'tasks.assignee_choose': 'Choose someone',
    'tasks.priority_label': 'Priority',
    'tasks.event_label': 'Event',
    'tasks.event_none': 'Business work — no event',
    'tasks.starts_label': 'Starts',
    'tasks.due_label': 'Due',
    'tasks.far_future_warning': 'That due date is more than a year away. If you meant it, say so — otherwise check the year.',
    'tasks.far_future_confirm': 'Yes, I meant that date',
    'tasks.delete': 'Delete',
    'tasks.delete_confirm_title': 'Delete “{description}”?',
    'tasks.delete_confirm_body': 'The task goes for good. Who it was assigned to, and when it moved, is kept.',
    'tasks.delete_confirm_submit': 'Delete for good',
    'tasks.delete_confirm_cancel': 'Keep it',
    'tasks.showing_page': 'Showing {from}–{to} of {total} tasks · page {page} of {pages}',
    'tasks.previous': 'Previous page',
    'tasks.next': 'Next page',
    'tasks.status.open': 'Open',
    'tasks.status.in_progress': 'In progress',
    'tasks.status.completed': 'Completed',
    'tasks.status.cancelled': 'Cancelled',
    'tasks.priority.low': 'Low',
    'tasks.priority.medium': 'Medium',
    'tasks.priority.high': 'High',
    /* STORY-024. Every one of these pairs a figure with the BASIS it was
       computed on, because a bare number on a dashboard is how a wrong one
       survives for months. Translators: the basis line is never optional and
       never a tooltip. */
    'metrics.heading': 'Where the business stands',
    'metrics.nothing_yet': 'No events have run yet, so there is nothing to average. Counts above are real.',
    'metrics.not_available': 'Not available',
    'metrics.down': '{amount} down',
    'metrics.total_events': 'Events',
    'metrics.total_events_basis': '{upcoming, plural, one {# upcoming} other {# upcoming}}, {completed, plural, one {# completed} other {# completed}}, {other, plural, one {# idea or cancelled} other {# ideas or cancelled}}',
    'metrics.upcoming_events': 'Upcoming',
    'metrics.upcoming_events_basis': 'Planned and confirmed. An idea is not counted.',
    'metrics.completed_events': 'Completed',
    'metrics.completed_events_basis': 'Nights that have happened.',
    'metrics.total_income': 'Income in {currency}',
    'metrics.total_income_basis': 'Everything that came in, whether or not it names an event.',
    'metrics.total_expense': 'Expense in {currency}',
    'metrics.total_expense_basis': 'Everything that went out, overhead included.',
    'metrics.total_profit': 'Profit in {currency}',
    'metrics.total_profit_basis': 'Everything recorded, overhead included. This is the figure on the “How it adds up” tab.',
    'metrics.attributed_profit': 'From events in {currency}',
    'metrics.attributed_profit_basis': 'Only money filed against an event that ran. Less than the profit above by whatever the overhead came to.',
    'metrics.average_profit': 'Average per event in {currency}',
    'metrics.average_profit_basis': '{count, plural, =0 {No events counted} one {Averaged over # event} other {Averaged over # events}} — cancelled events and ideas are left out.',
    'metrics.most_profitable': 'Best night in {currency}',
    'metrics.most_profitable_basis': 'Made {amount}.',
    'metrics.most_profitable_tie': '{count, plural, other {# events tied}}, each making {amount}.',
    'metrics.tie_separator': ', ',
    'metrics.chart_profit_caption': 'Every event that ran, by what it made. Bars run right of the line for a profit and left for a loss; each is labelled with its own figure.',
    'metrics.chart_budget_caption': 'What has been spent against what was planned. The bar runs past the mark when spending has.',
    'metrics.budget_used': '{percent} of the {currency} budget used — {spent} of {budget}.',
    'metrics.budget_zero': 'The {currency} budget is zero, so there is no share to show.',
    'metrics.budget_not_set': 'No {currency} budget is set, so there is nothing to measure {spent} against.',
    'metrics.budget_over': '{amount} over.',
    'money.tab_categories': 'Categories',
    'money.tabs_label': 'Money views',
    'categories.explainer': 'A category belongs to one side. The picker on an entry only ever offers the ones that apply, so an “in” category can never end up on an “out”.',
    'categories.add_heading': 'New category',
    'categories.name_label': 'What is it called?',
    'categories.add_submit': 'Add category',
    'categories.name_required': 'A name is needed.',
    /* Names the one it collided with, because the comparison ignores case and
     * accents — "Baskı" and "Baski" are one category here, and being refused
     * without being shown which row reads as a bug rather than as a duplicate. */
    'categories.duplicate_named': '“{name}” is already there. Two spellings of one name would split every total between them.',
    'categories.duplicate': 'That category already exists on this side.',
    'categories.income_heading': 'Money in',
    'categories.expense_heading': 'Money out',
    'categories.none': 'None on this side yet.',
    'categories.rename': 'Rename',
    'categories.rename_label': 'A new name for “{name}”',
    /* Said where the decision is made. This is the reason a Category is a record
     * and not a string copied onto each entry. */
    'categories.rename_hint': 'Every entry filed under it takes the new name too.',
    'categories.save': 'Save',
    'categories.cancel': 'Cancel',
    'categories.delete': 'Delete',
    'categories.delete_failed': 'That category was not deleted. Nothing changed.',
    /* The count, never a bare "in use" — one is a decision somebody can make and
     * the other is not. Deleting anyway is not offered: the money would go with
     * it. */
    'categories.in_use': '{count, plural, one {# entry is filed under this} other {# entries are filed under this}}. Move them to another category first, or keep this one.',
    /* ── STORY-022: what an Event made ────────────────────────────────────────
     * The first genuinely valuable number in the product. Two figures side by
     * side — what it was expected to make and what it did — because the source
     * stored both and never put them together, so the gap was invisible. */
    'event_money.heading': 'What it made',
    'event_money.table_caption': 'Income, expense and profit for this event, by currency',
    'event_money.currency_column': 'Currency',
    'event_money.profit_column': 'Profit',
    /* Zero is a fact, not a blank — and this is most events most of the time, so
     * it must not read as something having gone wrong. */
    'event_money.nothing_yet': 'Nothing recorded against this event yet. Income, expense and profit all read zero, which is a fact rather than a gap.',
    /* Said in words, never by colour alone. A red number is invisible to a
     * greyscale screen and to a screen reader alike — design.md. */
    'event_money.loss': '{amount} down',
    /* "Not set" and "0% used" are different facts and the second is a lie about
     * the first, so no division happens here at all. */
    'event_money.budget_not_set': 'No budget set for this event, so there is nothing to measure spending against.',
    'event_money.budget_used': '{percent} of the budget used — {spent} of {budget}.',
    'event_money.budget_zero': 'The budget is {budget}, so a share of it cannot be worked out.',
    /* A percentage spanning two currencies would need a rate this product does
     * not have, so the rest is named as uncompared rather than folded in. */
    'event_money.budget_not_set_for': 'Spending in {currencies} is not counted against that budget — a share across two currencies would need an exchange rate.',
    'event_money.revenue_short': 'Expected {expected}, took {actual} — {gap} short.',
    'event_money.revenue_over': 'Expected {expected}, took {actual} — {gap} over.',
    'people.title': 'People',
    'people.new': 'Add someone',
    'people.add_heading': 'Somebody new',
    'people.add_submit': 'Save them',
    'people.add_cancel': 'Not now',
    'people.name_label': 'What is their name?',
    'people.name_required': 'A name is needed — anything you will recognise.',
    /* Said out loud, because the whole point is that a name is enough. */
    'people.rest_optional': 'Everything else is optional. A name on its own is a real record.',
    'people.phone_label': 'Phone',
    /* Until the workspace has a country of its own, a national number cannot be
     * read — so the hint asks for the form that always can be. */
    'people.phone_hint': 'With the country code, like +90 532 123 45 67.',
    'people.phone_needs_region': 'Add the country code — for example +90 before a Turkish number.',
    'people.phone_invalid': 'That does not look like a phone number.',
    'people.email_label': 'Email',
    'people.notes_label': 'Notes',
    /* Says what becomes of it, in the box where it is typed. This field is where
     * special-category data lands in practice — a dietary requirement implies
     * religion, an access need implies disability — and the customer is the
     * controller: making that visible where the decision is made is what turns
     * their lawful basis into something they can act on. ADR-0017.
     *
     * Translator note: "encrypted" means the product cannot read it back without
     * its key, not that it is hidden from the team. Both halves matter and
     * neither should be dropped for length. */
    'people.notes_hint': 'Dietary needs, access requirements, anything worth remembering. Encrypted, and never searchable.',
    /* The row says one exists; the note itself arrives only when the record is
     * opened. Without this a saved note is invisible until somebody clicks Edit
     * on each row in turn. */
    'people.has_note': 'Has a note',
    /* Never a blank. A note rendered as nothing looks exactly like a note nobody
     * wrote, and saving over it would destroy what could not be read. */
    /* The read failed outright — different from a note that decrypts to
     * nothing and different again from one that cannot be decrypted. All three
     * end at the same place: do not save over what you cannot see. */
    'people.notes_load_failed': 'This note could not be loaded. Saving now would leave it alone — close this and try again.',
    'people.notes_unreadable': 'This note could not be read. Saving now would replace it — leave it and tell whoever runs this workspace.',
    'people.search_label': 'Search by name',
    'people.edit': 'Edit',
    'people.save': 'Save',
    'people.cancel': 'Cancel',
    'people.delete': 'Delete',
    'people.showing': 'Showing {shown} of {total}',
    /* Counted and shown, never acted on. Automatic retention is off by default
     * and is the customer's decision — they are the controller. */
    'people.dormant': 'Contacts with no activity for over two years: {count}. Nothing happens to them unless you say so.',
    'people.on_guest_list': 'They are on a guest list. Events they attended: {count}. Deleting them would take the headcount and the money with it.',
    /* ── STORY-019: exporting and erasing one guest ───────────────────────────
     * The customer is the controller for their guest data and we are the
     * processor. When a guest asks their organiser to be forgotten, the
     * organiser must be able to act unaided — building this as a support ticket
     * would make us a bottleneck in somebody else's legal obligation. */
    'people.export': 'Export',
    /* Says what the file holds BEFORE producing it, because "everything about
     * this person" means something different to whoever asked for it. */
    'people.export.title': 'Export everything held about {name}',
    'people.export.body': 'Their contact record — name, phone, email, note — and {count, plural, =0 {no attendances, because they are not on any guest list yet} one {their one attendance, with the event, their status and their pax} other {every one of their # attendances, with the event, their status and their pax}}. Structured and machine-readable, on every plan.',
    'people.export.close': 'Close',
    'people.export.failed': 'That export could not be produced. Nothing was changed.',
    /* One sentence, one source, shown on BOTH screens. STORY-019 asks for that
     * explicitly: worded separately the two would drift, and the honesty is the
     * point of it. A name typed into a task description or a supplier field is
     * personal data the system cannot find, and neither exporting nor erasing
     * the contact touches it. Implying completeness would be a false assurance
     * about somebody else's legal obligation. */
    'people.free_text_caveat': 'Free text written elsewhere — a task description, a supplier name on a transaction — may also name them, and cannot be found automatically. Those are worth a look by hand.',
    'people.erase.title': 'Erase {name}?',
    /* Names what survives as well as what does not. An irreversible action
     * described only by what it destroys is one nobody can weigh. */
    'people.erase.body': '{count, plural, =0 {Nothing depends on this record, so it is deleted outright.} one {They are on one guest list. The name, phone, email and note are removed for good; the attendance stays, with nobody behind it, so the headcount and the money attached to it are unchanged.} other {They are on # guest lists. The name, phone, email and note are removed for good; the attendances stay, with nobody behind them, so every headcount and the money attached is unchanged.}}',
    'people.erase.irreversible': 'This cannot be undone. It is not a flag that can be turned back — after it, the record does not hold who they were.',
    'people.erase.submit': 'Erase for good',
    'people.erase.cancel': 'Keep them',
    'people.erase.failed': 'That erasure did not happen. Nothing was changed.',
    'people.erase.done_deleted': 'Erased. The record is gone.',
    'people.erase.done_anonymised': 'Erased. {count, plural, one {# attendance stays} other {# attendances stay}}, with nobody behind them, and every total is unchanged.',
    /* A Person whose subject has been severed. ADR-0015 keeps the record and
     * empties the name, so every screen renders this one fixed label instead —
     * never a string the server stored, which could not be translated. */
    'people.erased_label': 'Someone who has been erased',
    'people.empty.title': 'Nobody here yet',
    'people.empty.body': 'This is the list you pick from when you invite people to an event, so you stop retyping names.',
    'people.empty.action': 'Add the first person',
    /* Different from an empty book, and telling somebody the wrong one reads as
     * records having disappeared. */
    'people.no_match.title': 'Nobody matches that',
    'people.no_match.body': 'There are people here — just none with a name like that.',
    'people.no_match.action': 'Clear the search',
    'people.cap.lead': 'The {plan} plan removes the contact limit.',
    'people.cap.limit': 'Free holds 100 contacts.',
    /* The way round it without paying, which the prompt requires and which is
     * genuinely the right advice for most people who hit this. */
    'people.cap.alternative': 'You can also delete contacts you no longer need. Nothing else on the free plan is capped.',
    'people.cap.see_plans': 'See the plans',
    'venues.title': 'Venues',
    'venues.add_new': 'Add a venue',
    'venues.add_heading': 'A new venue',
    'venues.add_submit': 'Save it',
    'venues.add_cancel': 'Not now',
    'venues.name_label': 'What is this place called?',
    'venues.name_required': 'Give it a name — anything you will recognise.',
    'venues.name_taken': 'Another venue already has that name.',
    'venues.timezone_label': 'Timezone',
    /* Null is a real answer, not a missing one. */
    'venues.timezone_inherited': 'Same as the workspace',
    'venues.timezone_disabled': '{city} — no longer offered',
    'venues.timezone_hint_owner': 'Only the timezones this workspace offers. Add more in Settings → Timezones.',
    /* A Member cannot enable one — that is Owner-only and deliberate — so the
     * hint names who can rather than offering a control that would be refused. */
    'venues.timezone_hint_member': 'Only the timezones this workspace offers. An Owner can add more in Settings → Timezones.',
    'venues.timezone_warning': 'Changing this changes how events here are displayed. Nothing that happened moves — only how it is shown.',
    'venues.event_count': 'Events held here: {count}',
    'venues.offered': 'Offered',
    'venues.hidden': 'Hidden',
    /* The badge beside the name. States the consequence rather than repeating the
     * switch's own word, which would be a second element saying nothing. */
    'venues.badge_hidden': 'Not offered on new events',
    'venues.toggle': 'Offer {name} on new events',
    'venues.edit': 'Edit',
    'venues.save': 'Save',
    'venues.cancel': 'Cancel',
    'venues.delete': 'Delete',
    'venues.in_use': 'Events are held here: {count}. Hide it instead — nothing is lost.',
    'venues.empty.title': 'No venues yet',
    /* Says the other way in, so nobody feels obliged to set them all up first. */
    'venues.empty.body': 'You can add one here, or make one as you write an event — whichever comes first.',
    'venues.empty.action': 'Add your first venue',
    'venues.picker_label': 'Where is it?',
    'venues.picker_none': 'Nowhere yet',
    /* A hidden venue an event already uses stays listed and stays selected,
     * marked — omitting it would blank the field on the next unrelated edit. */
    'venues.picker_hidden': '{name} — hidden',
    /* The list failed to load. The current venue is still shown, seeded from the
     * event itself, so the field is not wrong — it is just not the whole list. */
    'venues.picker_unavailable': 'The venue list could not be loaded, so only this one is shown.',
    /* A name that already existed returns that record untouched, so a timezone
     * typed alongside it does not apply. Said, never swallowed. */
    'venues.reused_zone': '{name} already exists, and reads in {zone}. That is the zone that applies.',
    'venues.reused_inherits': '{name} already exists, and uses the workspace timezone. That is the zone that applies.',
    'events.detail.rejected': 'That change was refused. Check the fields and try again.',
    /* A number nobody could parse is a MISTAKE, not a clear. Saying so is what
     * stops a grouped number wiping a figure somebody already saved. */
    'events.detail.money_invalid': 'That does not look like an amount.',
    'events.detail.money_decimals': 'That is more decimal places than this currency has.',
    'events.detail.money_too_large': 'That amount is too large.',
    /* And for somebody who has none, where that instruction cannot be followed:
     * on Free there is one seat, so they cannot invite the person they would
     * promote. Their way out is closing the workspace — STORY-010 — and saying it
     * is not built yet is the honest answer rather than a remedy that loops. */
    'settings.leave.only_member': 'You are the only person in this workspace, so leaving would leave it with nobody. Closing it is the way out, and that is not built yet.',
    /* ── plans — STORY-031 ──────────────────────────────────────────────────────
     * Prices and limits are never written here: they come from the catalogue the
     * API serves, because a price in two places drifts. What lives here is the
     * chrome around them. */
    'plans.title': 'Plans',
    'plans.body': 'Every feature is on every plan. What changes is how much of it you can run at once.',
    'plans.tax_note': 'Prices exclude tax. The rate depends on where you are, and is worked out at checkout.',
    'plans.monthly': 'per month',
    'plans.annual': 'per year',
    'plans.annual_saving': 'Two months free on annual billing.',
    /* Checkout — STORY-032. */
    'plans.choose': 'Choose {plan}',
    'plans.choose_annual': 'Choose {plan}, billed annually',
    'plans.per_year_billed': '{amount} billed yearly',
    'plans.opening_checkout': 'Opening checkout…',
    'plans.checkout_failed': 'Checkout could not be opened. Nothing has been charged — please try again.',
    'plans.checkout_forbidden': 'Only an Owner can change the plan for this workspace.',
    'plans.sign_in_to_upgrade': 'Sign in to choose a plan.',
    'plans.back_to_workspace': 'Back to workspace',
    /* Billing — STORY-032. */
    'billing.title': 'Billing',
    'billing.loading': 'Loading…',
    /* The billing trail — STORY-032, STORY-034. One key per whole sentence, and
     * the actor is a parameter rather than a concatenated fragment: Turkish
     * attaches its suffixes to the word, so a line assembled in English word
     * order cannot be reassembled. */
    'billing.history.heading': 'What has happened',
    'billing.history.loading': 'Loading the history…',
    'billing.history.unavailable': 'The history could not be loaded just now. Nothing has changed — please try again.',
    'billing.history.empty': 'Nothing has happened to this subscription yet.',
    'billing.history.moved_to': 'Moved to {status}',
    'billing.history.checkout_started': 'Checkout started',
    'billing.history.cancellation_requested': 'Cancellation requested',
    'billing.history.cancellation_lifted': 'Cancellation lifted',
    'billing.history.transitioned': 'Changed',
    'billing.history.by': 'by {who}',
    /* Not a person, and saying so beats a blank: "who did this" is the question,
     * and "not one of us" is a real answer to it. */
    'billing.history.by_provider': 'by Stripe',
    'billing.history.by_erased': 'a closed account',
    'billing.none': 'This workspace is on the Free plan and has no subscription.',
    /* A subscription that ENDED is not the same as never having had one, and one
     * sentence for both would tell somebody who paid last month that we have no
     * record of it. Says the workspace is back on Free, because that is the part
     * they need — the previous plan is over and this is what they have now. */
    'billing.ended': 'Your subscription has ended and this workspace is back on the Free plan.',
    'billing.unavailable': 'Your billing details could not be loaded just now. Nothing has changed — please try again.',
    'billing.checkout_done': 'Thanks — your payment was received. The plan updates as soon as the provider confirms it, usually within a few seconds.',
    'billing.checkout_cancelled': 'Checkout was cancelled. Nothing has been charged.',
    'billing.plan': 'Plan',
    'billing.status': 'Status',
    'billing.amount': 'Amount',
    /* Said next to the amount, because the two numbers genuinely differ and the
     * customer sees the larger one — added 2026-09-05 after a live test payment
     * of $19.00 charged $20.54.
     *
     * Stripe is the merchant of record (ADR-0007) and adds the tax due where the
     * customer is, which we neither compute nor hold: the figure above is the
     * plan's price, and the invoice from Stripe is the one that matches a bank
     * statement. Saying nothing would leave somebody comparing the two to
     * conclude they had been overcharged. */
    'billing.amount_excludes_tax': 'Before any tax. Stripe adds the tax due where you are, so your statement and their invoice may show more.',
    'billing.renews': 'Renews',
    'billing.status_active': 'Active',
    'billing.status_past_due': 'Payment overdue',
    'billing.status_unpaid': 'Unpaid — the plan has ended',
    'billing.status_incomplete': 'Not yet active',
    'billing.status_canceled': 'Cancelled',
    'billing.managed_by_stripe': 'Payments and invoices are handled by Stripe, who are the seller of record.',
    'billing.see_plans': 'See plans',
    /* Cancelling — STORY-034. */
    'billing.ends_on': 'Ends on {when}. You keep everything until then.',
    'billing.renews_on': 'Renews on {when}.',
    'billing.cancel': 'Cancel subscription',
    'billing.cancel_confirm': 'Stop this subscription renewing?',
    'billing.cancel_confirm_body': 'It runs until the end of the period you have paid for, and nothing is deleted. You can start it again at any time before then.',
    'billing.cancel_confirm_yes': 'Yes, stop renewing',
    'billing.cancel_confirm_no': 'Keep it',
    'billing.resume': 'Resume subscription',
    'billing.cancel_requested': 'Asked. It updates here as soon as the provider confirms it, usually within a few seconds.',
    'billing.cancel_failed': 'That could not be sent to the payment provider. Nothing has changed — please try again.',
    'nav.billing': 'Billing',
    'plans.free': 'Free',
    'plans.pro': 'Pro',
    'plans.business': 'Business',
    'plans.enterprise': 'Enterprise',
    'plans.active_events': 'Active events',
    'plans.seats': 'Seats',
    'plans.contacts': 'Contacts',
    'plans.unlimited': 'Unlimited',
    /* No grammatical agreement, so it is correct at every count in both locales
     * and does not pull the ICU trigger in this file's header. */
    'plans.usage': 'You are using {used} of {limit}',
    'plans.usage_unlimited': 'You are using {used}',
    'plans.current': 'Your current plan',
    /* Said plainly, because a Member with twenty ideas and one confirmed Event
     * will otherwise think the count is broken — STORY-031. */
    /* Kept in step with `ACTIVE_EVENT_STATUSES` — STORY-012 narrowed the meter
     * and this sentence still described the old rule, which put "ideas count too"
     * directly above a usage figure that did not count them. Copy that
     * contradicts the code is worse than none. */
    'plans.active_event_meaning': 'An active event is one that is Planned or Confirmed. Ideas are free, and completing or cancelling an event releases its place.',
    'plans.teams_need_pro': 'Free includes one seat, so working with anybody else needs Pro or above.',
    'plans.at_limit': 'You are at the limit on this plan.',
    'plans.near_limit': 'You are close to the limit on this plan.',
    'plans.loading': 'Loading the plans…',
    /* ── invitations — STORY-007 ────────────────────────────────────────────── */
    'invite.heading': 'Invite somebody',
    'invite.email_label': 'Their email address',
    'invite.role_label': 'What they can do',
    'invite.submit': 'Send the invitation',
    'invite.sent': 'The invitation is on its way.',
    'invite.pending': 'Waiting to be accepted',
    'invite.code_label': 'Code for {email}',
    'invite.code_hint': 'Read this out if the email does not arrive. It expires with the invitation.',
    'invite.revoke': 'Withdraw the invitation to {email}',
    'invite.revoked': 'The invitation is withdrawn.',
    'invite.none': 'Nobody is waiting to accept.',
    'invite.remove': 'Remove {name} from this workspace',
    'invite.remove_confirm': 'Remove {name}?',
    /* Says what SURVIVES, not just what goes. The fear when removing somebody is
     * losing their work, and the answer is that none of it moves. */
    'invite.remove_explains': 'Their account stays, and everything they wrote here stays with it. They lose access to this workspace only.',
    'invite.remove_cancel': 'Keep them',
    /* Asking to join — STORY-008. The Owner's half. */
    'requests.heading': 'Asking to join',
    'requests.body': 'Somebody with the workspace ID has asked to be let in. A pending request grants nothing until it is approved.',
    'requests.asked': 'Asked {when}',
    'requests.approve': 'Approve {name}',
    'requests.decline': 'Decline {name}',
    'requests.approve_label': 'Approve',
    'requests.decline_label': 'Decline',
    /* {plan} is the plan that LIFTS the limit, never the current one — that is
     * what the API puts in the field, matching `invite.seat_limit_lead`. Phrased
     * as an upsell for that reason: written as a statement about the current plan
     * it said "there is no seat for them on Pro" to an Owner on Free, naming the
     * one plan where there IS room.
     *
     * On Free every approval refuses — the founder holds the only seat — so this
     * has to read as "teams start on a paid plan" rather than as a broken
     * button. EPIC-001 risk 7. */
    'requests.seat_limit': 'Let them in on {plan}',
    'requests.seat_limit_limit': 'Your plan has no seat for them. The request stays here until you have room — nothing is lost.',
    'invite.remove_yes': 'Remove them',
    'invite.last_owner': 'A workspace always keeps at least one Owner, and never empties. Promote somebody else first.',
    'invite.seat_limit_lead': 'Work with more people on {plan}',
    'invite.seat_limit_limit': 'This workspace is using every seat its plan includes.',
    'invite.seat_limit_alternative': 'Or remove somebody who has left — a freed seat is available straight away.',
    'invite.see_plans': 'See plans',
    'accept.title': 'Join the team',
    'accept.body': 'Enter the code you were given, or use the link in your invitation email.',
    'accept.code_label': 'Invitation code',
    'accept.code_hint': 'Eight characters, like ABCD-2345. Upper or lower case, dash optional.',
    'accept.submit': 'Join',
    'accept.joining': 'Checking the invitation…',
    'accept.joined': 'You are in {name}.',
    /* One message for expired, used, withdrawn, unknown, and issued to a
     * different address. The server does not distinguish them and neither may
     * this — the difference would say whether a credential was ever real. */
    'accept.invalid': 'That invitation is no longer valid. Ask whoever invited you for a new one.',
    'accept.wrong_account': 'Invitations are tied to the address they were sent to. Sign in with that address, or ask for one to this address.',
    'auth.forgot.title': 'Reset your password',
    'auth.forgot.body': 'Tell us the address you sign in with and we will send a link.',
    'auth.forgot.email_label': 'Email address',
    'auth.forgot.submit': 'Send the link',
    'auth.forgot.back': 'Back to sign in',
    'auth.forgot.sent_title': 'Check your email',
    /* Says the same thing whether or not the address has an account, and the
     * sentence admits it rather than implying a link definitely went out. A
     * confident "we have sent you a link" would be a lie half the time and the
     * lie is what leaks the answer. */
    'auth.forgot.sent_body': 'If there is an account for that address, a link is on its way. The link works once and expires in an hour.',
    'auth.reset.title': 'Choose a new password',
    'auth.reset.password_label': 'New password',
    'auth.reset.submit': 'Set the password',
    'auth.reset.done_title': 'Password changed',
    /* Naming the consequence rather than hiding it. Somebody resetting because
     * they suspect a compromise needs to know the other sessions are gone. */
    'auth.reset.done_body': 'You are signed out everywhere else. Sign in with your new password.',
    /* One message for expired, already used, and never existed. Distinguishing
     * them would say whether a token was ever real. */
    'auth.reset.bad_link_title': 'That link no longer works',
    'auth.reset.bad_link_body': 'Reset links work once and expire after an hour. Ask for a new one.',
    'auth.reset.request_another': 'Ask for a new link',
    'error.required': 'This cannot be empty.',
    'error.email_invalid': 'That does not look like an email address.',
    'error.password_too_short': 'Use at least 8 characters.',
    'error.rate_limited': 'Too many attempts. Try again in a little while.',
    'error.network': 'We could not reach the server. Check your connection and try again.',
    /* Never a raw exception in the UI — `coding.md`. */
    'error.unexpected': 'Something went wrong. Try again.',
    'common.loading': 'Loading…',
    'common.submitting': 'Working…',
    /* ── email ──────────────────────────────────────────────────────────────────
     * Rendered server-side in the RECIPIENT's stored locale, never the locale of
     * the request that triggered it — `i18n.md`. A Member invited by a Turkish
     * colleague gets their mail in their own language, and somebody resetting a
     * password gets it in theirs regardless of which browser asked.
     *
     * That is why `users.locale` is a column rather than a request-scoped value,
     * and it is the reason these keys live in the shared package rather than in
     * `apps/api`. */
    'email.verify.subject': 'Confirm your email address',
    'email.verify.body': 'Welcome to eventuley.\n\nConfirm your email address to carry on:\n{link}\n\nThe link works once and expires in 24 hours.\nIf you did not sign up, ignore this message — nothing was created in your name that this link cannot undo by being left alone.',
    'email.already_registered.subject': 'You already have an eventuley account',
    'email.already_registered.body': 'Somebody tried to sign up with this address, and it is already registered.\n\nIf that was you, sign in instead:\n{link}\n\nIf you have forgotten your password, you can reset it from that page.\nIf it was not you, no action is needed. Nothing has changed on your account.',
    'email.reset.subject': 'Reset your eventuley password',
    'email.reset.body': 'Somebody asked to reset the password for this account.\n\nChoose a new one here:\n{link}\n\nThe link works once and expires in an hour. Setting a new password signs you out everywhere else.\n\nIf it was not you, ignore this message. Your password has not changed, and nobody can change it without this link.',
};
/* Turkish is not a courtesy locale — `i18n.md` is explicit that it fails loudly
 * where other languages fail quietly. Two things to watch in this file:
 * suffixes attach to the word, so a sentence assembled from fragments in
 * English word order cannot be reassembled; and there is one plural form after
 * a number, so "5 gün" and never "5 günler". Both are reasons the catalogue
 * holds whole sentences rather than pieces. */
const tr = {
    'app.name': 'eventuley',
    'auth.sign_up.title': 'Hesabınızı oluşturun',
    'auth.sign_up.email_label': 'E-posta adresi',
    'auth.sign_up.name_label': 'Adınız',
    'auth.sign_up.password_label': 'Parola',
    'auth.sign_up.password_hint': 'En az 8 karakter. Büyük harf veya sembol zorunlu değil.',
    'auth.sign_up.agreement': 'Hizmet şartları ve veri işleme sözleşmesi hazırlanıyor ve henüz yayımlanmadı. Yayımlandıklarında geçerli olacak ve kabul etmeniz istenecek.',
    'auth.sign_up.agreement_published': 'Hesap oluşturarak hizmet şartlarını ve veri işleme sözleşmesini kabul etmiş olursunuz.',
    'auth.sign_up.read_terms': 'Hizmet şartlarını okuyun',
    'auth.sign_up.read_dpa': 'Veri işleme sözleşmesini okuyun',
    'legal.back': 'Girişe dön',
    'legal.loading': 'Belge yükleniyor…',
    'legal.unavailable_title': 'Kullanılamıyor',
    'legal.unavailable_body': 'Bu belge henüz yayımlanmadı. Bu bağlantı size gönderildiyse, gönderen kişiden güncel sürümü isteyin.',
    'legal.version': '{version} sürümü, {date} tarihinden itibaren geçerli',
    'auth.sign_up.submit': 'Hesap oluştur',
    'auth.sign_up.have_account': 'Zaten hesabınız var mı?',
    'auth.sign_up.sign_in_link': 'Giriş yapın',
    'auth.verify.title': 'E-postanızı kontrol edin',
    'auth.verify.sent_to': '{email} adresine bir bağlantı gönderdik.',
    'auth.verify.why': 'Çalışma alanı oluşturmak veya bir alana katılmak doğrulama gerektirir.',
    'auth.verify.verifying': 'E-posta adresiniz doğrulanıyor…',
    'auth.verify.verified_title': 'E-posta doğrulandı',
    'auth.verify.verified_body': 'Artık bir çalışma alanı oluşturabilir veya bir alana katılabilirsiniz.',
    'auth.verify.continue': 'Devam et',
    'auth.verify.expired_title': 'Bu bağlantının süresi doldu',
    'auth.verify.expired_body': 'Giriş yapın, size yenisini gönderelim.',
    'auth.verify.invalid_title': 'Bu bağlantı geçerli değil',
    'auth.verify.invalid_body': 'Adresin tamamını e-postadan kopyaladığınızdan emin olun.',
    'auth.verify.go_to_sign_in': 'Giriş sayfasına git',
    'auth.sign_in.title': 'Giriş yap',
    'auth.sign_in.email_label': 'E-posta adresi',
    'auth.sign_in.password_label': 'Parola',
    'auth.sign_in.submit': 'Giriş yap',
    'auth.sign_in.forgot': 'Parolanızı mı unuttunuz?',
    'auth.sign_in.no_account': 'Henüz hesabınız yok mu?',
    'auth.sign_in.create_link': 'Hesap oluşturun',
    'auth.sign_in.failed': 'Bu e-posta ve parola bir hesapla eşleşmiyor.',
    'auth.sign_in.locked_title': 'Çok fazla deneme',
    'auth.sign_in.locked_body': 'Bu adres için giriş 15 dakika duraklatıldı. Sizseniz, parolanızı sıfırlayarak daha erken girebilirsiniz.',
    'workspace.choose.title': 'Çalışma alanı oluşturun veya bir alana katılın',
    'workspace.choose.body': 'Çalışma alanı ekibinizin etkinlikleri, kişileri ve parasıdır. Birden fazla alana ait olabilirsiniz.',
    'workspace.choose.create': 'Çalışma alanı oluştur',
    'workspace.choose.join_code': 'Davet koduyla katıl',
    'workspace.choose.join_code_why': 'Henüz yapılmadı. Davet kodu, bir ekibe doğrudan katılmanızı sağlayacak.',
    'workspace.choose.join_id': 'Çalışma alanı kimliğiyle katılma isteği gönder',
    'workspace.choose.sign_out': '{email} hesabından çıkış yap',
    'workspace.invited.heading': 'Davet edildiniz',
    'workspace.invited.expires': '{role} olarak · {when} tarihinde sona eriyor',
    'workspace.invited.join': 'Katıl',
    'workspace.waiting.heading': 'Henüz bir çalışma alanında değilsiniz',
    'workspace.waiting.status': 'Onay bekliyor',
    'workspace.waiting.asked': '{when} istendi · bir Sahibin onaylaması gerekiyor',
    'workspace.waiting.withdraw': '{id} alanına gönderilen isteği geri çek',
    'workspace.waiting.meanwhile': 'O zamana kadar erişiminiz olmaz. Bu arada kendi alanınızı kurabilirsiniz.',
    'workspace.switch.title': 'Çalışma alanlarınız',
    'workspace.switch.body': 'Tek giriş, istediğiniz kadar ekip.',
    'workspace.switch.another': 'Başka bir yer',
    'workspace.switch.disambiguate': 'Kimlik {id}',
    'workspace.switch.failed': 'Bu çalışma alanı artık size açık değil.',
    'workspace.switch.entered': 'Şimdi {name} içindesiniz.',
    'workspace.join.title': 'Bir çalışma alanına katılma isteği gönderin',
    'workspace.join.body': 'Alandaki herkes kimliği adres çubuğundan okuyabilir. Buraya yapıştırın.',
    'workspace.join.id_label': 'Çalışma alanı kimliği',
    'workspace.join.id_hint': 'Bir Sahibin onaylaması gerekir. Onaylanana kadar erişiminiz olmaz.',
    'workspace.join.id_invalid': 'Bu bir çalışma alanı kimliği değil. Şuna benzer: {example}.',
    'workspace.join.submit': 'İsteği gönder',
    'workspace.join.submitting': 'Gönderiliyor…',
    'workspace.join.sent': 'O çalışma alanı varsa bir Sahip isteğinizi görecek. Onaylayana kadar hiçbir şey olmaz.',
    'workspace.join.rate_limited': 'Çok fazla istek gönderdiniz. Başka bir kimlik denemeden önce bekleyin.',
    'workspace.join.back': 'Geri',
    'workspace.create.title': 'Çalışma alanınıza ad verin',
    'workspace.create.name_label': 'Çalışma alanı adı',
    'workspace.create.currency_label': 'Para birimi',
    'workspace.create.currency_common': 'Yaygın',
    'workspace.create.currency_all': 'Tüm para birimleri',
    'workspace.create.currency_empty': 'Eşleşen para birimi yok.',
    'workspace.create.currency_results': 'Eşleşen para birimi: {count}',
    'workspace.create.currency_hint': 'Para kaydı girildikten sonra değiştirilemez. Diğer her şey düzenlenebilir kalır.',
    'workspace.create.timezone_detected': 'Saatler, bu cihazdan algılanan {timezone} saat diliminde gösterilecek. Ayarlardan değiştirebilirsiniz.',
    'workspace.create.submit': 'Çalışma alanı oluştur',
    'workspace.create.back': 'Geri',
    'workspace.create.name_too_long': '60 karakter veya daha az kullanın.',
    'workspace.create.currency_required': 'Bir para birimi seçin.',
    'workspace.create.currency_unknown': 'Bu, tanıdığımız bir para birimi kodu değil.',
    'nav.label': 'Ana menü',
    'nav.open': 'Menüyü aç',
    'nav.close': 'Menüyü kapat',
    'nav.calendar': 'Takvim',
    'nav.events': 'Etkinlikler',
    'nav.venues': 'Mekânlar',
    'nav.people': 'Kişiler',
    'nav.tasks': 'Görevler',
    'nav.money': 'Para',
    'nav.inventory': 'Envanter',
    'nav.settings': 'Ayarlar',
    'nav.account': 'Hesap',
    'nav.switch_workspace': 'Çalışma alanı değiştir',
    'nav.sign_out': 'Çıkış yap',
    'role.owner': 'Sahip',
    'role.member': 'Üye',
    'nav.not_built_title': '{destination} henüz yapılmadı',
    'nav.not_built_body': 'Bu destinasyon üründe var olduğu için listeleniyor. Ekranı sonraki bir hikâyede geliyor.',
    'settings.title': 'Ayarlar',
    'settings.general': 'Çalışma alanı',
    'settings.name_label': 'Çalışma alanı adı',
    'settings.rename': 'Adı kaydet',
    'settings.renamed': 'Çalışma alanı adı kaydedildi.',
    'settings.owner_only': 'Bunu yalnızca bir Sahip değiştirebilir.',
    'settings.retention.heading': 'Etkin olmayan misafirleri silme',
    'settings.retention.explanation': 'Artık gelmeyen bir misafiri ne kadar süre saklayacağınıza siz karar verirsiniz. Siz açmadıkça kapalıdır ve açana kadar hiçbir şey silinmez.',
    'settings.retention.label': 'Misafiri şu süreden sonra sil',
    'settings.retention.off': 'Asla — herkesi sakla',
    'settings.retention.months': '{count, plural, other {# ay hareketsizlik}}',
    /* Turkish has ONE form after a number, so only 'other' — the same reason
     * 'settings.retention.months' above has only one. */
    'over_limit.title': 'Bu çalışma alanı planının sınırlarını aşıyor',
    'over_limit.meter': '{meter}: {limit} sınırına karşılık {used}. {count, plural, other {Sığması için # tanesini kaldırın}}.',
    'over_limit.events': 'Etkin etkinlikler',
    'over_limit.seats': 'Üyeler',
    'over_limit.contacts': 'Kişiler',
    'over_limit.refused': 'Bu değişiklik kaydedilmedi — çalışma alanı sınırlara sığana kadar salt okunur.',
    'over_limit.control_disabled': 'Bu çalışma alanı planının sınırlarını aşıyor ve sığana kadar salt okunur.',
    'over_limit.exits': 'Hiçbir şey silinmedi. İhtiyacınız olmayanları kaldırabilir veya daha büyük bir plana geçebilirsiniz.',
    'settings.retention.warning': 'Bu, o süre boyunca hiç hareketi olmayan misafirlerin adını, telefonunu, e-postasını ve notlarını kalıcı olarak siler. Katılım kayıtları ve kişi sayılarınız korunur. Geri alınamaz.',
    'settings.retention.save': 'Kaydet',
    'settings.retention.saved': 'Kaydedildi. Bir sonraki taramada kullanılacak.',
    'settings.retention.owner_only': 'Misafirlerin ne kadar süre saklanacağını yalnızca bir Sahip değiştirebilir.',
    'settings.members': 'Üyeler',
    'settings.members_body': 'Bu çalışma alanında kimler var ve her biri ne yapabilir.',
    'settings.role_label': '{name} için rol',
    'settings.last_owner': 'Bir çalışma alanında her zaman en az bir Sahip kalır. Önce başka birini yükseltin.',
    'settings.role_changed': '{name} artık {role}.',
    'settings.owner_explains': 'Sahipler ekipte kimin olduğunu, neye ödeme yapıldığını ve çalışma alanının nasıl kurulduğunu yönetir. Diğer her şeyi — etkinlikler, kişiler, para, görevler ve ekipman — her Üye yapabilir.',
    'settings.leave.heading': 'Bu çalışma alanından ayrıl',
    'settings.leave.body': 'Erişiminiz sona erer. Kaydettiğiniz her şey adınıza atfedilmiş olarak çalışma alanında kalır ve diğer çalışma alanlarınıza dokunulmaz.',
    'settings.leave.submit': 'Bu çalışma alanından ayrıl',
    'settings.leave.confirm': '{name} alanından ayrılınsın mı?',
    'settings.leave.explains': 'Geri dönmek için yeni bir davete ihtiyacınız olur.',
    'settings.leave.yes': 'Ayrıl',
    'settings.leave.cancel': 'Kal',
    'settings.leave.last_owner': 'Bu çalışma alanının tek Sahibi sizsiniz. Önce başka birini Sahip yapın.',
    'settings.currencies.heading': 'Para birimleri',
    'settings.currencies.search_label': 'Tüm para birimlerinde ara',
    'settings.currencies.search_placeholder': 'Kod veya ad',
    'settings.currencies.hint': 'Ekibiniz yalnızca burada listelenen para birimlerinden seçebilir. Çalıştığınız birimleri eklemek için yukarıdan arayın — dünyadaki her para birimi kullanılabilir.',
    'settings.currencies.empty': '“{query}” ile eşleşen para birimi yok. JPY gibi bir kod ya da Yen gibi bir ad deneyin.',
    'settings.currencies.count_enabled': 'Etkin para birimleri: {count}',
    'settings.currencies.count_matches': '“{query}” ile eşleşen para birimleri: {count}',
    'settings.currencies.own': 'Çalışma alanı para birimi',
    'settings.currencies.own_locked': 'Çalışma alanının para birimi. Yukarıdaki Çalışma alanı bölümünden değiştirin.',
    'settings.currencies.last_locked': 'Son para birimi. Bir çalışma alanında her zaman en az bir tane kalır.',
    'settings.currencies.member_note': 'Burada listelenen her para birimini kullanabilir ve her tutarı okuyabilirsiniz. Yeni bir tane eklemek Sahibin kararıdır — bir Sahibe sorun, burada görünür.',
    'settings.timezones.heading': 'Saat dilimleri',
    'settings.timezones.search_label': 'Tüm saat dilimlerinde ara',
    'settings.timezones.search_placeholder': 'Dilim, şehir veya fark',
    'settings.timezones.hint': 'Ekibiniz yalnızca burada listelenen saat dilimlerinden seçebilir. Çalıştığınız dilimleri eklemek için yukarıdan arayın — her IANA dilimi kullanılabilir.',
    'settings.timezones.empty': '“{query}” ile eşleşen saat dilimi yok. Tokyo gibi bir şehir ya da Asia/Tokyo gibi bir dilim deneyin.',
    'settings.timezones.count_enabled': 'Etkin saat dilimleri: {count}',
    'settings.timezones.count_matches': '“{query}” ile eşleşen saat dilimleri: {count}',
    'settings.timezones.own': 'Çalışma alanı saat dilimi',
    'settings.timezones.own_locked': 'Çalışma alanının saat dilimi. Yukarıdaki Çalışma alanı bölümünden değiştirin.',
    'settings.timezones.last_locked': 'Son saat dilimi. Bir çalışma alanında her zaman en az bir tane kalır.',
    'settings.timezones.member_note': 'Burada listelenen her saat dilimini kullanabilirsiniz. Yeni bir tane eklemek Sahibin kararıdır — bir Sahibe sorun, burada görünür.',
    'settings.enabled.group_on': 'Etkin',
    'settings.enabled.group_off': 'Etkin değil',
    'settings.enabled.add': '{name} ekle',
    'settings.enabled.toggle': '{name} sunulmayı bıraksın',
    'settings.enabled.locked_label': '{name} sunuluyor ve kapatılamaz',
    'settings.enabled.on': 'Etkin',
    'settings.enabled.always_on': 'Her zaman açık',
    'settings.enabled.owner_only': 'Yalnızca Sahip',
    'settings.enabled.unused': 'Henüz kullanılmadı',
    'settings.enabled.used': 'Kullanan kayıtlar: {count}',
    'settings.enabled.decimals_none': 'Ondalık basamak yok',
    'settings.enabled.decimals': 'Ondalık basamak: {count}',
    'settings.enabled.confirm_title': '{name} sunulmayı bıraksın mı?',
    'settings.enabled.confirm_body': 'Zaten kullanan kayıtlar onu korur — yalnızca yenilerinde size sunulmaz. İstediğiniz zaman geri açabilirsiniz.',
    'settings.enabled.confirm_yes': 'Kapat',
    'settings.enabled.confirm_no': 'Kalsın',
    'settings.enabled.failed': 'Bu değişiklik kaydedilmedi. Liste hâlâ sunucudaki durumu gösteriyor.',
    'settings.enabled.unreadable': 'Bu çalışma alanının neler sunduğunu okuyamadık. Yeniden denemek için sayfayı yenileyin.',
    'settings.enabled.more': 'Gösterilmeyen diğerleri: {count}. Daraltmak için yazmayı sürdürün.',
    'account.export.heading': 'Verilerinizi indirin',
    'account.export.body': 'Sizin hakkınızda tuttuğumuz her şey: hesabınız, kabul ettikleriniz ve ait olduğunuz her çalışma alanı.',
    'account.export.excludes': 'Bir çalışma alanının etkinliklerini, konuklarını veya parasını içermez. Bunlar çalışma alanına aittir; onları Sahibinden isteyin.',
    'account.export.submit': 'Verilerimi indir',
    'account.export.ready': 'Buyurun',
    'account.export.rate_limited': 'Bu çok fazla indirme oldu. Daha sonra tekrar deneyin.',
    'account.close.heading': 'Hesabınızı kapatın',
    'account.close.body': 'Her yerdeki erişiminiz sona erer. Kaydettikleriniz, adınız çıkarılmış olarak her çalışma alanında kalır.',
    'account.close.sole_owner': '{name} alanının tek Sahibi sizsiniz',
    'account.close.sole_owner_body': 'Ona ne olacağına karar verin. Bir çalışma alanı asla Sahipsiz bırakılmaz ve birinin ayrılmasının yan etkisi olarak kapatılmaz.',
    'account.close.what_happens': '{name} alanına ne olacak',
    'account.close.choice_close': 'İçindeki her şeyle birlikte {name} alanını da kapat',
    'account.close.choice_promote': 'Devret — önce başka birini Sahip yapacağım',
    'account.close.close_warning': 'Etkinlikleri, konukları, parası ve ekipmanı onunla birlikte gider. Erişimini kaybedecek kişiler: {count}. Varsa ücretli abonelik bunun bir parçası olarak iptal edilir.',
    'account.close.open_members': 'Üyeler’i aç ve birini yükselt',
    'account.close.password_label': 'Parolanız',
    'account.close.wrong_password': 'Bu parola eşleşmiyor.',
    'account.close.undecided': 'Tek Sahibi olduğunuz her çalışma alanıyla ilgili soruyu yanıtlayın.',
    'account.close.blocked': 'Bu işlem sürebilmesi için yukarıda {names} hakkındaki soruyu yanıtlayın.',
    'account.close.grace': 'Hiçbir şey {days} gün boyunca yok edilmez. O tarihten önce giriş yapmak işlemi iptal eder.',
    'account.close.submit': 'Hesabımı kapat',
    'account.closed.title': 'Hesabınız {when} tarihinde kapanıyor',
    'account.closed.body': 'O tarihe kadar hiçbir şey yok edilmez ve giriş yapmak her şeyi geri getirir. Sonrasında geriye kalan anonimdir: başkalarının bağlı olduğu kayıtlar, adınız çıkarılmış olarak.',
    'account.closed.undo': 'Hesabımı koru',
    'events.title': 'Etkinlikler',
    'events.new': 'Yeni etkinlik',
    'events.no_date': 'Henüz tarih yok',
    'events.filters.show_finished': 'Tamamlananları ve iptal edilenleri göster',
    'events.filters.more': 'Filtreler ve sıralama',
    'events.filters.status_legend': 'Durum',
    'events.filters.venue': 'Mekân',
    'events.filters.any_venue': 'Herhangi bir mekân',
    'events.filters.organiser': 'Düzenleyen',
    'events.filters.any_organiser': 'Herkes',
    'events.filters.when': 'Ne zaman',
    'events.filters.any_time': 'Her zaman',
    'events.filters.last_7': 'Son 7 gün',
    'events.filters.last_30': 'Son 30 gün',
    'events.filters.last_90': 'Son 90 gün',
    'events.filters.last_365': 'Son bir yıl',
    'events.filters.sort': 'Sıralama',
    'events.filters.sort_date': 'Tarih',
    'events.filters.sort_name': 'Ad',
    'events.filters.sort_status': 'Durum',
    'events.filters.date_asc': 'Önce en erken',
    'events.filters.date_desc': 'Önce en geç',
    'events.filters.name_asc': 'A’dan Z’ye',
    'events.filters.name_desc': 'Z’den A’ya',
    'events.filters.status_asc': 'Önce fikirler',
    'events.filters.status_desc': 'Önce iptal edilenler',
    'events.filters.active': 'Şuna göre filtreleniyor:',
    'events.filters.remove': '{filter} filtresini kaldır',
    'events.filters.clear': 'Tüm filtreleri temizle',
    'events.filters.chip_status': 'Durum',
    'events.filters.chip_venue': 'Mekân',
    'events.filters.chip_organiser': 'Düzenleyen',
    'events.filters.chip_when': 'Ne zaman',
    'events.filters.none_match.title': 'Bu filtrelere uyan bir şey yok',
    'events.filters.none_match.body': 'Burada etkinlikler var — yalnızca istediklerinize uyan yok.',
    'events.list.showing': '{total} etkinlikten {from}–{to} arası gösteriliyor',
    'events.list.previous': 'Önceki sayfa',
    'events.list.next': 'Sonraki sayfa',
    'events.list.venue_unknown': 'Henüz mekân yok',
    'events.empty.title': 'Burada henüz bir şey yok',
    'events.empty.body': 'Her şeyin merkezinde etkinlikler var — konuklar, görevler ve para hepsi bir etkinliğe bağlı. Sadece bir adla başlayın.',
    'events.empty.action': 'İlk etkinliğinizi ekleyin',
    'events.capture.heading': 'Yeni bir etkinlik',
    'events.capture.name_label': 'Adı ne?',
    'events.capture.rest_optional': 'Gerisi bekleyebilir. Öğrendiğinizde istediğiniz zaman ekleyebilirsiniz.',
    'events.capture.date_label': 'Tarih',
    'events.capture.time_label': 'Başlangıç saati',
    'events.capture.time_needs_date': 'Saat için tarih gerekir. Bir tarih ekleyin ya da saati silin.',
    'events.capture.name_required': 'Bir ad verin — tanıyacağınız herhangi bir şey.',
    'events.capture.submit': 'Kaydet',
    'events.capture.cancel': 'Şimdi değil',
    'events.capture.gone': 'Bu çalışma alanı artık size açık değil.',
    'events.status.idea': 'Fikir',
    'events.status.planned': 'Planlandı',
    'events.status.confirmed': 'Kesinleşti',
    'events.status.completed': 'Tamamlandı',
    'events.status.cancelled': 'İptal edildi',
    'events.lifecycle.heading': 'Durumu',
    'events.lifecycle.label': 'Durum',
    'events.lifecycle.save': 'Değiştir',
    'events.lifecycle.incomplete': 'Karar vermeden önce bilmekte fayda var:',
    'events.lifecycle.missing_date': 'Henüz tarih yok.',
    'events.lifecycle.missing_venue': 'Henüz mekân yok.',
    'events.lifecycle.early_completion': 'Bu henüz gerçekleşmedi. Erken kapatmak sorun değil — tarih olduğu gibi kalır.',
    'events.lifecycle.no_active_events': 'Planınızda etkin etkinlik hakkı kalmadı. {plan} planı bu sınırı yükseltir. Hiçbir şey kaybolmadı — bu etkinlik yerinde kaldı.',
    'events.lifecycle.moved_underneath': 'Bunu önce başkası taşıdı. Şu anda {status}. Değiştirmeden önce bir daha bakın.',
    'events.lifecycle.history_heading': 'Başına neler geldi',
    'events.lifecycle.history_line': '{from} → {to}',
    'events.lifecycle.history_by': '{who} · {when}',
    'events.person_removed': 'Ayrılmış biri',
    'events.detail.back': 'Etkinliklere dön',
    'events.detail.missing': 'Bu etkinlik burada değil',
    'events.detail.save': 'Kaydet',
    'events.detail.saved': 'Kaydedildi.',
    'events.detail.name_heading': 'Ad',
    'events.detail.when_heading': 'Ne zaman',
    'events.detail.when_hint': 'Bu etkinliğin bir mekânı olana kadar saati çalışma alanının saat diliminde okunur.',
    'events.detail.budget_heading': 'Bütçe',
    'events.detail.budget_hint': 'Ne kadara mal olmasını ve ne kadar getirmesini beklediğiniz. İkisi de isteğe bağlıdır ve her biri kendi para birimini korur.',
    'events.detail.budget_label': 'Planlanan bütçe',
    'events.detail.budget_currency_label': 'Bütçe para birimi',
    'events.detail.revenue_label': 'Beklenen gelir',
    'events.detail.revenue_currency_label': 'Gelir para birimi',
    'events.detail.currency_disabled': '{code} — artık sunulmuyor',
    'events.detail.notes_heading': 'Notlar',
    'events.detail.notes_label': 'Hatırlanmaya değer her şey',
    'events.detail.reads_in': 'Buradaki saatler {zone} saatine göre okunur.',
    'guests.heading': 'Kimler geliyor',
    /* Turkish takes ONE form after a number — "5 kişi", never "5 kişiler" — so
     * the plural has a single `other` branch and that is correct rather than
     * lazy. This is the message ICU exists for. */
    'guests.totals': '{guests, plural, =0 {Henüz kimse davet edilmedi} other {# davetli}}{guests, plural, =0 {} other {, {pax, plural, other {# kişi}}}}',
    'guests.add_label': 'Birini ekle',
    'guests.add_hint': 'Kişi defterinde arayın ya da defterde olmayan bir ad yazın.',
    'guests.no_match': 'Kişi defterinde buna uyan kimse yok.',
    'guests.results': '{count, plural, other {# eşleşme}}',
    'guests.create': '“{name}” kişisini yeni olarak ekle',
    'guests.gone': 'Bu kişi artık burada değil. Yeniden aramayı deneyin.',
    'guests.contact_cap': 'Planınız kişi sınırında olduğu için bu kişi kaydedilemedi. Kişi defterinde zaten olan biri yine de eklenebilir.',
    'guests.pax_failed': 'Bu kişi sayısı kaydedilmedi. Görünen sayı kayıttaki sayıdır.',
    'guests.remove_failed': 'Bu kişi listeden çıkarılamadı. Hiçbir şey değişmedi.',
    'guests.empty': 'Bu listede henüz kimse yok.',
    'guests.none_confirmed': 'Henüz kimse onaylamadı, bu yüzden sayı sıfır. Davet edilen herkes hâlâ listede.',
    'guests.already_invited': '{name} zaten bu listede, başka bir sayfada.',
    'guests.status_short': 'Durum',
    'guests.payment_short': 'Ödeme',
    'guests.pax_short': 'Kişi',
    'guests.status_label': '{name} nerede duruyor',
    'guests.page_size': 'Sayfa başına kişi',
    'guests.page_size_n': '{count, plural, other {sayfada #}}',
    'guests.page_size_all': 'Hepsi',
    'guests.showing': '{total, plural, other {# davetliden}} {from}–{to} arası · sayfa {page} / {pages}',
    'guests.previous': 'Önceki sayfa',
    'guests.next': 'Sonraki sayfa',
    'guests.pax_label': '{name} kendisi dahil kaç kişi getiriyor',
    'guests.pax_invalid': 'Birden başlayan tam bir kişi sayısı.',
    'guests.pax_implausible': 'Tek bir davette {pax, plural, other {# kişi}}. Bir kez daha bakmakta fayda var ama kaydedildi.',
    'guests.remove': 'Çıkar',
    'guests.status.invited': 'Davet edildi',
    'guests.status.confirmed': 'Onaylandı',
    'guests.status.attended': 'Katıldı',
    'guests.status.cancelled': 'İptal edildi',
    'guests.payment.unpaid': 'Ödenmedi',
    'guests.payment.paid': 'Ödendi',
    'guests.payment.comped': 'Ücretsiz',
    'guests.payment.refunded': 'İade edildi',
    'guests.payment_label': '{name} ödeme yaptı mı',
    'guests.payment_failed': 'Bu ödeme durumu kaydedilmedi. Görünen, kayıttaki durumdur.',
    'guests.payments': '{paid} ödendi · {unpaid} ödenmedi · {comped} ücretsiz · {refunded} iade edildi',
    'money.title': 'Para',
    'money.record': 'Kayıt ekle',
    'money.record_heading': 'Ne oldu',
    'money.record_submit': 'Kaydet',
    'money.record_cancel': 'Şimdi değil',
    'money.direction.income': 'Giren para',
    'money.direction.expense': 'Çıkan para',
    'money.direction_label': 'Giren mi, çıkan mı?',
    'money.amount_label': 'Ne kadar',
    'money.amount_invalid': 'Bu bir tutar değil. Söyleyeceğiniz gibi yazın.',
    'money.amount_above_zero': 'Sıfırdan büyük bir tutar. Paranın çıktığını eksi işareti değil, “giren mi çıkan mı” belirler.',
    'money.currency_label': 'Para birimi',
    'money.currency_hint': 'Bir Sahip, Ayarlar → Para birimleri bölümünden ekleyebilir.',
    'money.currency_not_enabled': 'Bu para birimi bu çalışma alanında henüz kullanılmıyor. Bir Sahip Ayarlar’dan ekleyebilir.',
    'money.date_label': 'Ne zaman',
    'money.date_hint': 'Bulunduğunuz yerdeki tarihiyle, olayın gerçekleştiği gün.',
    'money.category_label': 'Ne için',
    'money.category_none': 'Birini seçin',
    'money.check_the_form': 'Bu formdaki bir şey kabul edilmedi. Yukarıdaki alanları kontrol edin.',
    'money.category_wrong_direction': 'Bu kategori diğer tarafa ait. Kategoriler ayrı tutulur ki bir “giren” toplamı sessizce “çıkan” içermesin.',
    'money.event_label': 'Hangi etkinlik',
    'money.event_overhead': 'Genel gider — tek bir etkinlik değil',
    'money.event_hint': 'Tek bir gecenin gideri olmayan her şey için genel giderde bırakın.',
    'money.event_hint_truncated': 'En son {count} etkinlik gösteriliyor. Daha eskiler burada henüz sunulmuyor.',
    'money.description_label': 'Neydi',
    'money.counterparty_label': 'Kim (isteğe bağlı)',
    'money.counterparty_hint': 'Bir tedarikçi veya ödeme yapılan kişi.',
    'money.totals_label': 'Toplamlar',
    'money.amount_in': 'Giren {amount}',
    'money.amount_out': 'Çıkan {amount}',
    'money.row_meta': '{date} · {category}',
    'money.delete': 'Sil',
    'money.delete_failed': 'Bu kayıt silinmedi. Hiçbir şey değişmedi.',
    'money.delete_confirm_title': '“{description}” silinsin mi?',
    'money.delete_confirm_body': 'Kayıt kalıcı olarak silinir ve tutarı geri getirilemez. Yalnızca kimin, ne zaman sildiği saklanır.',
    'money.delete_confirm_submit': 'Kalıcı olarak sil',
    'money.delete_confirm_cancel': 'Kalsın',
    'reconciliation.heading': 'Toplamlar nasıl birbirini tutuyor',
    'reconciliation.intro': 'Etkinlik başına kâr yalnızca bir etkinliğe işlenen parayı sayar. İşletme kârı her şeyi sayar. Aradaki fark genel giderdir ve sonradan fark edilmeye bırakılmak yerine burada gösterilir.',
    'reconciliation.nothing_yet': 'Henüz hiçbir kayıt yok, bu yüzden üçü de sıfır.',
    'reconciliation.unavailable': 'Bu rakamlar birbirini tutmadı, bu yüzden hiçbiri gösterilmiyor. Kayıtlarınızda bir sorun yok — hata bir sorguda ve bildirildi.',
    'reconciliation.table_caption': 'Etkinliklere işlenen paradan kâr, hiçbirine işlenmeyenden kâr ve ikisinin toplamı — para birimi başına bir satır.',
    'reconciliation.currency_column': 'Para birimi',
    'reconciliation.attributed_column': 'Etkinliklerden',
    'reconciliation.overhead_column': 'Genel gider',
    'reconciliation.organisation_column': 'İşletmenin tamamı',
    'reconciliation.adds_up_cost': '{currency} cinsinden: etkinliklerden {attributed}, eksi {overhead} genel gider, işletmenin tamamı için {organisation} eder.',
    'reconciliation.adds_up_income': '{currency} cinsinden: etkinliklerden {attributed}, artı hiçbir etkinliğe ait olmayan {overhead}, işletmenin tamamı için {organisation} eder.',
    'reconciliation.down': '{amount} zarar',
    'reconciliation.show_overhead': '{currency} genel giderinin neyden oluştuğunu göster',
    'reconciliation.hide_overhead': '{currency} genel giderinin neyden oluştuğunu gizle',
    'reconciliation.no_overhead': 'Genel gider olarak işlenmiş bir şey yok.',
    'reconciliation.overhead_truncated': '{total} genel gider kaydından {count} tanesi gösteriliyor.',
    'money.showing_page': '{total, plural, other {# kayıttan}} {from}–{to} arası · sayfa {page} / {pages}',
    'money.previous': 'Önceki sayfa',
    'money.next': 'Sonraki sayfa',
    'money.empty.title': 'Henüz kayıt yok',
    'money.empty.body': 'Giren ve çıkan her tutar, ne için olduğuyla birlikte — böylece toplamlar hatırlanan değil, gerçek kayıtlardan oluşur.',
    'money.tab_ledger': 'Kayıtlar',
    'money.tab_reconciliation': 'Nasıl toplanıyor',
    'money.tab_dashboard': 'Genel görünüm',
    'language.label': 'Dil',
    'language.heading': 'Dil',
    'language.hint': 'Bu ayar sizin, bir çalışma alanının değil — bulunduğunuz her ekipte ve her cihazda sizi takip eder.',
    'language.not_saved': 'Ekran seçtiğiniz dilde, ancak hesabınıza kaydedemedik. Kaydedilene kadar başka bir cihaza taşınmayacak.',
    'inventory.title': 'Ekipman',
    'inventory.tabs_label': 'Ekipman görünümleri',
    'inventory.tab_outstanding': 'Hâlâ dışarıda',
    'inventory.tab_items': 'Sahip olduklarımız',
    'inventory.outstanding_count': '{count, plural, =0 {Dışarıda bir şey yok.} other {# şey geri gelmedi.}}',
    'inventory.nothing_out': 'Her şey yerinde.',
    'inventory.out_for': '{date} tarihinde {event} etkinliğine gitti — {days, plural, other {# gün önce}}',
    'inventory.out_for_undated': 'Henüz tarihi olmayan {event} etkinliğine gitti',
    'inventory.mark_returned': 'Geri geldi',
    'inventory.just_returned': 'Az önce geldi',
    'inventory.packing_heading': 'Sonrası için hazırlanan',
    'inventory.packing_hint': 'Bunlar henüz gerçekleşmemiş etkinlikler için. Kayıp değiller, bu yüzden yukarıda sayılmıyorlar.',
    'inventory.packing_for': '{event} için',
    'inventory.return.followup': 'Geri geldi olarak işaretlendi. Durumu nasıl?',
    'inventory.return.skip': 'Olduğu gibi kalsın',
    'inventory.condition_label': 'Durum',
    'inventory.condition_unchanged': 'Olduğu gibi kalsın',
    'inventory.condition.excellent': 'Mükemmel',
    'inventory.condition.good': 'İyi',
    'inventory.condition.fair': 'İdare eder',
    'inventory.condition.lightly_damaged': 'Hafif hasarlı',
    'inventory.condition.problematic': 'Sorunlu',
    'inventory.condition.lost': 'Kayıp',
    'inventory.add': 'Bir şey ekle',
    'inventory.close_form': 'Kapat',
    'inventory.submit': 'Kaydet',
    'inventory.cancel': 'Vazgeç',
    'inventory.refused': 'Kaydedilmedi. Adı ve maliyeti kontrol edin.',
    'inventory.nothing_yet': 'Henüz kayıtlı bir şey yok.',
    'inventory.name_label': 'Nedir',
    'inventory.category_label': 'Kategori',
    'inventory.owner_label': 'Sahibi kim',
    'inventory.holder_label': 'Kimde',
    'inventory.nobody': 'Belirli biri değil',
    'inventory.row_meta': '{condition}',
    'inventory.cost': 'Maliyet {amount}',
    'inventory.cost_from_books': 'Maliyet {amount}, satın alan gider kaydından',
    'inventory.held_by': '{name} kişisinde',
    'inventory.former_member': 'Eski bir üye',
    'inventory.delete': 'Sil',
    'inventory.delete_in_use': 'Bu bir etkinliğe götürüldü, bu yüzden kayıt saklanıyor. Bunun yerine kayıp olarak işaretleyin — maliyeti geçmişte kalır ve yeniden bulunabilir.',
    'inventory.delete_confirm_title': '“{name}” silinsin mi?',
    'inventory.delete_confirm_body': 'Kayıt kalıcı olarak silinir, maliyeti de onunla gider.',
    'inventory.delete_confirm_submit': 'Kalıcı olarak sil',
    'inventory.delete_confirm_cancel': 'Kalsın',
    'inventory.save': 'Değişiklikleri kaydet',
    'inventory.edit': 'Değiştir',
    'inventory.notes_label': 'Notlar',
    'inventory.purchased_label': 'Alınma tarihi',
    'inventory.cost_label': 'Maliyeti',
    'inventory.currency_label': 'Para birimi',
    'inventory.cost_invalid': 'Bu bir tutar değil.',
    'inventory.expense_label': 'Satın alan gider kaydı',
    'inventory.expense_none': 'Yok — hediye ya da kayıtlarımızdan eski',
    'inventory.expense_hint': 'Bağlamak maliyeti tek bir yerde tutar. Başka bir şeye bağlı giderler listelenmez.',
    'inventory.expense_linked_hint': 'Maliyet ve tarih o gider kaydından gelir ve düzeltildiğinde onu takip eder. Kendi değerinizi girmek için bağlantıyı kaldırın.',
    'inventory.loadout_heading': 'Ekipman',
    'inventory.loadout_empty': 'Bu etkinliğe henüz bir şey gitmiyor.',
    'inventory.loadout_add': 'Bir şey götür',
    'inventory.loadout_return_all': 'Hepsi geri geldi',
    'inventory.loadout_returned': '{date} tarihinde geldi',
    'inventory.loadout_remove': 'Listeden çıkar',
    'inventory.loadout_choose': 'Ne gideceğini seçin',
    'inventory.loadout_submit': 'Listeye ekle',
    'calendar.title': 'Takvim',
    'calendar.previous': 'Önceki ay',
    'calendar.next': 'Sonraki ay',
    'calendar.today': 'Bu ay',
    'calendar.day_label': '{date} — {count, plural, =0 {hiçbir şey yok} other {# şey var}}',
    'calendar.nothing_this_month': 'Bu ay planlanmış ya da zamanı gelmiş bir şey yok.',
    'calendar.nothing_that_day': 'O gün bir şey yok.',
    'calendar.show_whole_month': 'Tüm ayı göster',
    'calendar.agenda_month': 'Bu ay',
    'calendar.agenda_day': '{date}',
    'calendar.event_on': 'Etkinlik · {date}',
    'calendar.event_undated': 'Etkinlik · henüz tarih yok',
    'calendar.task_due': 'Görev · son tarih {date} · {priority}',
    'calendar.task_undated': 'Görev · henüz tarih yok · {priority}',
    'calendar.undated_heading': 'Henüz tarihi yok',
    'calendar.undated_hint': 'Tarihi olmayan fikirler ve tarihi olmayan bitmemiş işler. Kaybolmasınlar diye buradalar.',
    'tasks.title': 'Görevler',
    'tasks.new': 'Bir görev yaz',
    'tasks.close_form': 'Kapat',
    'tasks.submit': 'Görevi kaydet',
    'tasks.cancel': 'Vazgeç',
    'tasks.refused': 'Bu görev kaydedilmedi. Tarihleri ve kime ait olduğunu kontrol edin.',
    'tasks.none_yet': 'Henüz yazılmış bir şey yok.',
    'tasks.none_match': 'Açık olan filtrelere uyan görev yok.',
    'tasks.clear_filters': 'Filtreleri temizle',
    'tasks.hidden_overdue': '{count, plural, other {Bu filtreler # gecikmiş görevi gizliyor.}}',
    'tasks.show_hidden': 'Göster',
    'tasks.overdue': 'Gecikmiş',
    'tasks.due': 'Son tarih {date}',
    'tasks.row_meta': '{assignee} · {priority}',
    'tasks.assignee_erased': 'Eski bir üye',
    'tasks.assignee_left': 'Bu kişi çalışma alanından ayrıldı. İş ayrılmadı.',
    'tasks.changed_underneath': 'Siz bakarken bir başkası bu görevi değiştirdi. Hiçbir şeyin üzerine yazılmadı — nereye geldiğini görmek için yeniden açın.',
    'tasks.filters_label': 'Görevleri filtrele',
    'tasks.assignee_filter_label': 'Kimin',
    'tasks.assignee_mine': 'Benim',
    'tasks.assignee_everyone': 'Herkesin',
    'tasks.status_filter_label': 'Durum',
    'tasks.priority_filter_label': 'Öncelik',
    'tasks.status_label': 'Durum',
    'tasks.description_label': 'Yapılması gereken',
    'tasks.assignee_label': 'Kim yapacak',
    'tasks.assignee_choose': 'Birini seçin',
    'tasks.priority_label': 'Öncelik',
    'tasks.event_label': 'Etkinlik',
    'tasks.event_none': 'İşletme işi — etkinlik yok',
    'tasks.starts_label': 'Başlangıç',
    'tasks.due_label': 'Son tarih',
    'tasks.far_future_warning': 'Bu son tarih bir yıldan uzak. Gerçekten öyleyse onaylayın — değilse yılı kontrol edin.',
    'tasks.far_future_confirm': 'Evet, o tarihi kastettim',
    'tasks.delete': 'Sil',
    'tasks.delete_confirm_title': '“{description}” silinsin mi?',
    'tasks.delete_confirm_body': 'Görev kalıcı olarak silinir. Kime atandığı ve ne zaman değiştiği saklanır.',
    'tasks.delete_confirm_submit': 'Kalıcı olarak sil',
    'tasks.delete_confirm_cancel': 'Kalsın',
    'tasks.showing_page': '{total} görevden {from}–{to} gösteriliyor · sayfa {page}/{pages}',
    'tasks.previous': 'Önceki sayfa',
    'tasks.next': 'Sonraki sayfa',
    'tasks.status.open': 'Açık',
    'tasks.status.in_progress': 'Devam ediyor',
    'tasks.status.completed': 'Tamamlandı',
    'tasks.status.cancelled': 'İptal edildi',
    'tasks.priority.low': 'Düşük',
    'tasks.priority.medium': 'Orta',
    'tasks.priority.high': 'Yüksek',
    'metrics.heading': 'İşletme nerede duruyor',
    'metrics.nothing_yet': 'Henüz hiçbir etkinlik gerçekleşmedi, bu yüzden ortalaması alınacak bir şey yok. Yukarıdaki sayılar gerçek.',
    'metrics.not_available': 'Yok',
    'metrics.down': '{amount} zarar',
    'metrics.total_events': 'Etkinlik',
    'metrics.total_events_basis': '{upcoming, plural, other {# yaklaşan}}, {completed, plural, other {# tamamlanan}}, {other, plural, other {# fikir veya iptal}}',
    'metrics.upcoming_events': 'Yaklaşan',
    'metrics.upcoming_events_basis': 'Planlanan ve onaylanan. Fikir sayılmaz.',
    'metrics.completed_events': 'Tamamlanan',
    'metrics.completed_events_basis': 'Gerçekleşmiş geceler.',
    'metrics.total_income': '{currency} cinsinden gelir',
    'metrics.total_income_basis': 'Bir etkinliğe işlensin ya da işlenmesin, giren her şey.',
    'metrics.total_expense': '{currency} cinsinden gider',
    'metrics.total_expense_basis': 'Genel gider dahil, çıkan her şey.',
    'metrics.total_profit': '{currency} cinsinden kâr',
    'metrics.total_profit_basis': 'Genel gider dahil, kaydedilen her şey. “Nasıl toplanıyor” sekmesindeki rakam budur.',
    'metrics.attributed_profit': '{currency} cinsinden etkinliklerden',
    'metrics.attributed_profit_basis': 'Yalnızca gerçekleşen bir etkinliğe işlenen para. Yukarıdaki kârdan genel gider kadar azdır.',
    'metrics.average_profit': '{currency} cinsinden etkinlik başına ortalama',
    'metrics.average_profit_basis': '{count, plural, =0 {Sayılan etkinlik yok} other {# etkinlik üzerinden ortalama}} — iptal edilenler ve fikirler hariç.',
    'metrics.most_profitable': '{currency} cinsinden en iyi gece',
    'metrics.most_profitable_basis': '{amount} kazandırdı.',
    'metrics.most_profitable_tie': '{count, plural, other {# etkinlik başa baş}}, her biri {amount} kazandırdı.',
    'metrics.tie_separator': ', ',
    'metrics.chart_profit_caption': 'Gerçekleşen her etkinlik, kazandırdığına göre. Çubuklar kâr için çizginin sağına, zarar için soluna uzanır; her biri kendi rakamıyla etiketlidir.',
    'metrics.chart_budget_caption': 'Planlanana karşı harcanan. Harcama aştığında çubuk işareti geçer.',
    'metrics.budget_used': '{currency} bütçesinin {percent} kadarı kullanıldı — {budget} içinden {spent}.',
    'metrics.budget_zero': '{currency} bütçesi sıfır, bu yüzden gösterilecek bir oran yok.',
    'metrics.budget_not_set': '{currency} için bütçe belirlenmemiş, bu yüzden {spent} tutarını karşılaştıracak bir şey yok.',
    'metrics.budget_over': '{amount} aşıldı.',
    'money.tab_categories': 'Kategoriler',
    'money.tabs_label': 'Para görünümleri',
    'categories.explainer': 'Bir kategori tek bir tarafa aittir. Kayıttaki seçici yalnızca uygun olanları sunar, böylece bir “giren” kategorisi asla bir “çıkan” üzerine gelemez.',
    'categories.add_heading': 'Yeni kategori',
    'categories.name_label': 'Adı ne?',
    'categories.add_submit': 'Kategori ekle',
    'categories.name_required': 'Bir ad gerekli.',
    'categories.duplicate_named': '“{name}” zaten var. Bir adın iki yazımı, her toplamı ikiye böler.',
    'categories.duplicate': 'Bu kategori bu tarafta zaten var.',
    'categories.income_heading': 'Giren para',
    'categories.expense_heading': 'Çıkan para',
    'categories.none': 'Bu tarafta henüz yok.',
    'categories.rename': 'Yeniden adlandır',
    'categories.rename_label': '“{name}” için yeni bir ad',
    'categories.rename_hint': 'Bu kategoriye işlenmiş her kayıt da yeni adı alır.',
    'categories.save': 'Kaydet',
    'categories.cancel': 'İptal',
    'categories.delete': 'Sil',
    'categories.delete_failed': 'Bu kategori silinmedi. Hiçbir şey değişmedi.',
    'categories.in_use': 'Bu kategoriye işlenmiş {count, plural, other {# kayıt}} var. Önce onları başka bir kategoriye taşıyın ya da bunu koruyun.',
    'event_money.heading': 'Ne kazandırdı',
    'event_money.table_caption': 'Bu etkinliğin geliri, gideri ve kârı, para birimine göre',
    'event_money.currency_column': 'Para birimi',
    'event_money.profit_column': 'Kâr',
    'event_money.nothing_yet': 'Bu etkinlik için henüz kayıt yok. Gelir, gider ve kâr sıfır — bu bir boşluk değil, bir gerçek.',
    'event_money.loss': '{amount} zarar',
    'event_money.budget_not_set': 'Bu etkinlik için bütçe belirlenmemiş, bu yüzden harcamanın ölçüleceği bir şey yok.',
    'event_money.budget_used': 'Bütçenin %{percent} kısmı kullanıldı — {budget} içinden {spent}.',
    'event_money.budget_zero': 'Bütçe {budget} olduğu için oranı hesaplanamaz.',
    'event_money.budget_not_set_for': '{currencies} cinsinden harcamalar o bütçeye sayılmaz — iki para birimi arasında oran için kur gerekir.',
    'event_money.revenue_short': 'Beklenen {expected}, gerçekleşen {actual} — {gap} eksik.',
    'event_money.revenue_over': 'Beklenen {expected}, gerçekleşen {actual} — {gap} fazla.',
    'people.title': 'Kişiler',
    'people.new': 'Kişi ekle',
    'people.add_heading': 'Yeni biri',
    'people.add_submit': 'Kaydet',
    'people.add_cancel': 'Şimdi değil',
    'people.name_label': 'Adı ne?',
    'people.name_required': 'Bir ad gerekli — tanıyacağınız herhangi bir şey.',
    'people.rest_optional': 'Gerisi isteğe bağlı. Tek başına bir ad da gerçek bir kayıttır.',
    'people.phone_label': 'Telefon',
    'people.phone_hint': 'Ülke koduyla birlikte, örneğin +90 532 123 45 67.',
    'people.phone_needs_region': 'Ülke kodunu ekleyin — örneğin Türkiye için +90.',
    'people.phone_invalid': 'Bu bir telefon numarasına benzemiyor.',
    'people.email_label': 'E-posta',
    'people.notes_label': 'Notlar',
    'people.notes_hint': 'Beslenme ihtiyaçları, erişim gereksinimleri, hatırlanmaya değer her şey. Şifrelenir ve aranamaz.',
    'people.has_note': 'Notu var',
    'people.notes_load_failed': 'Bu not yüklenemedi. Şimdi kaydetmek nota dokunmaz — kapatıp yeniden deneyin.',
    'people.notes_unreadable': 'Bu not okunamadı. Şimdi kaydetmek notun üzerine yazar — dokunmayın ve bu çalışma alanını yönetene haber verin.',
    'people.search_label': 'Ada göre ara',
    'people.edit': 'Düzenle',
    'people.save': 'Kaydet',
    'people.cancel': 'Vazgeç',
    'people.delete': 'Sil',
    'people.showing': '{total} kişiden {shown} tanesi gösteriliyor',
    'people.dormant': 'İki yıldan uzun süredir hareketsiz kişiler: {count}. Siz söylemedikçe hiçbiri için bir şey yapılmaz.',
    'people.on_guest_list': 'Bu kişi bir konuk listesinde. Katıldığı etkinlikler: {count}. Silmek, sayıyı ve parayı da götürür.',
    'people.export': 'Dışa aktar',
    'people.export.title': '{name} hakkında tutulan her şeyi dışa aktar',
    'people.export.body': 'İletişim kaydı — ad, telefon, e-posta, not — ve {count, plural, =0 {henüz hiçbir konuk listesinde olmadığı için hiç katılım yok} other {etkinlik, durum ve kişi sayısıyla birlikte # katılımının tümü}}. Yapılandırılmış ve makine tarafından okunabilir, her planda.',
    'people.export.close': 'Kapat',
    'people.export.failed': 'Bu dışa aktarma oluşturulamadı. Hiçbir şey değişmedi.',
    'people.free_text_caveat': 'Başka yerlere yazılmış serbest metinler — bir görev açıklaması, bir işlemdeki tedarikçi adı — bu kişiyi de adlandırıyor olabilir ve otomatik olarak bulunamaz. Bunlara elle bakmakta fayda var.',
    'people.erase.title': '{name} silinsin mi?',
    'people.erase.body': '{count, plural, =0 {Bu kayda bağlı hiçbir şey yok, bu yüzden tümüyle silinir.} other {Bu kişi # konuk listesinde. Ad, telefon, e-posta ve not kalıcı olarak kaldırılır; katılımlar arkalarında kimse olmadan kalır, böylece her sayı ve ona bağlı para değişmez.}}',
    'people.erase.irreversible': 'Bu geri alınamaz. Geri çevrilebilecek bir işaret değildir — sonrasında kayıt, kişinin kim olduğunu tutmaz.',
    'people.erase.submit': 'Kalıcı olarak sil',
    'people.erase.cancel': 'Kalsın',
    'people.erase.failed': 'Bu silme gerçekleşmedi. Hiçbir şey değişmedi.',
    'people.erase.done_deleted': 'Silindi. Kayıt yok.',
    'people.erase.done_anonymised': 'Silindi. {count, plural, other {# katılım}} arkasında kimse olmadan kalır ve tüm toplamlar değişmez.',
    'people.erased_label': 'Silinmiş biri',
    'people.empty.title': 'Henüz kimse yok',
    'people.empty.body': 'Bir etkinliğe kişi davet ederken seçtiğiniz liste burasıdır; böylece adları tekrar tekrar yazmazsınız.',
    'people.empty.action': 'İlk kişiyi ekleyin',
    'people.no_match.title': 'Buna uyan kimse yok',
    'people.no_match.body': 'Burada kişiler var — yalnızca bu ada benzeyen yok.',
    'people.no_match.action': 'Aramayı temizle',
    'people.cap.lead': '{plan} planı kişi sınırını kaldırır.',
    'people.cap.limit': 'Ücretsiz plan 100 kişi tutar.',
    'people.cap.alternative': 'Artık ihtiyacınız olmayan kişileri de silebilirsiniz. Ücretsiz planda başka hiçbir şey sınırlı değil.',
    'people.cap.see_plans': 'Planlara bakın',
    'venues.title': 'Mekânlar',
    'venues.add_new': 'Mekân ekle',
    'venues.add_heading': 'Yeni bir mekân',
    'venues.add_submit': 'Kaydet',
    'venues.add_cancel': 'Şimdi değil',
    'venues.name_label': 'Bu yerin adı ne?',
    'venues.name_required': 'Bir ad verin — tanıyacağınız herhangi bir şey.',
    'venues.name_taken': 'Bu ad başka bir mekâna ait.',
    'venues.timezone_label': 'Saat dilimi',
    'venues.timezone_inherited': 'Çalışma alanıyla aynı',
    'venues.timezone_disabled': '{city} — artık sunulmuyor',
    'venues.timezone_hint_owner': 'Yalnızca bu çalışma alanının sunduğu saat dilimleri. Ayarlar → Saat dilimleri’nden ekleyin.',
    'venues.timezone_hint_member': 'Yalnızca bu çalışma alanının sunduğu saat dilimleri. Bir Sahip, Ayarlar → Saat dilimleri’nden ekleyebilir.',
    'venues.timezone_warning': 'Bunu değiştirmek buradaki etkinliklerin nasıl görüneceğini değiştirir. Olan hiçbir şey yer değiştirmez — yalnızca gösterimi değişir.',
    'venues.event_count': 'Burada yapılan etkinlikler: {count}',
    'venues.offered': 'Sunuluyor',
    'venues.hidden': 'Gizli',
    'venues.badge_hidden': 'Yeni etkinliklerde sunulmuyor',
    'venues.toggle': 'Yeni etkinliklerde {name} sunulsun',
    'venues.edit': 'Düzenle',
    'venues.save': 'Kaydet',
    'venues.cancel': 'Vazgeç',
    'venues.delete': 'Sil',
    'venues.in_use': 'Burada yapılan etkinlikler: {count}. Bunun yerine gizleyin — hiçbir şey kaybolmaz.',
    'venues.empty.title': 'Henüz mekân yok',
    'venues.empty.body': 'Buradan ekleyebilir ya da bir etkinlik yazarken oluşturabilirsiniz — hangisi önce gelirse.',
    'venues.empty.action': 'İlk mekânınızı ekleyin',
    'venues.picker_label': 'Nerede?',
    'venues.picker_none': 'Henüz bir yer yok',
    'venues.picker_hidden': '{name} — gizli',
    'venues.picker_unavailable': 'Mekân listesi yüklenemedi, bu yüzden yalnızca bu gösteriliyor.',
    'venues.reused_zone': '{name} zaten var ve {zone} saatine göre okunuyor. Geçerli olan bu.',
    'venues.reused_inherits': '{name} zaten var ve çalışma alanının saat dilimini kullanıyor. Geçerli olan bu.',
    'events.detail.rejected': 'Bu değişiklik reddedildi. Alanları kontrol edip tekrar deneyin.',
    'events.detail.money_invalid': 'Bu bir tutara benzemiyor.',
    'events.detail.money_decimals': 'Bu para biriminin sahip olduğundan daha fazla ondalık basamak.',
    'events.detail.money_too_large': 'Bu tutar çok büyük.',
    'settings.leave.only_member': 'Bu çalışma alanındaki tek kişi sizsiniz; ayrılırsanız alan boş kalır. Çıkış yolu alanı kapatmaktır ve bu henüz yapılmadı.',
    'plans.title': 'Planlar',
    'plans.body': 'Her özellik her planda var. Değişen, aynı anda ne kadarını çalıştırabildiğiniz.',
    'plans.tax_note': 'Fiyatlara vergi dâhil değildir. Oran bulunduğunuz yere göre değişir ve ödeme sırasında hesaplanır.',
    'plans.monthly': 'aylık',
    'plans.annual': 'yıllık',
    'plans.annual_saving': 'Yıllık ödemede iki ay ücretsiz.',
    /* Checkout — STORY-032. */
    'plans.choose': '{plan} seç',
    'plans.choose_annual': '{plan} seç, yıllık faturalandır',
    'plans.per_year_billed': 'Yıllık {amount} faturalandırılır',
    'plans.opening_checkout': 'Ödeme sayfası açılıyor…',
    'plans.checkout_failed': 'Ödeme sayfası açılamadı. Hiçbir ücret alınmadı — lütfen tekrar deneyin.',
    'plans.checkout_forbidden': 'Bu çalışma alanının planını yalnızca bir Sahip değiştirebilir.',
    'plans.sign_in_to_upgrade': 'Plan seçmek için oturum açın.',
    'plans.back_to_workspace': 'Çalışma alanına dön',
    /* Billing — STORY-032. */
    'billing.title': 'Faturalandırma',
    'billing.loading': 'Yükleniyor…',
    'billing.history.heading': 'Neler oldu',
    'billing.history.loading': 'Geçmiş yükleniyor…',
    'billing.history.unavailable': 'Geçmiş şu anda yüklenemedi. Hiçbir şey değişmedi — lütfen tekrar deneyin.',
    'billing.history.empty': 'Bu abonelikte henüz bir şey olmadı.',
    'billing.history.moved_to': '{status} durumuna geçti',
    'billing.history.checkout_started': 'Ödeme başlatıldı',
    'billing.history.cancellation_requested': 'İptal istendi',
    'billing.history.cancellation_lifted': 'İptal geri alındı',
    'billing.history.transitioned': 'Değişti',
    'billing.history.by': '{who} tarafından',
    'billing.history.by_provider': 'Stripe tarafından',
    'billing.history.by_erased': 'kapatılmış bir hesap',
    'billing.none': 'Bu çalışma alanı Ücretsiz planda ve aboneliği yok.',
    'billing.ended': 'Aboneliğiniz sona erdi ve bu çalışma alanı Ücretsiz plana döndü.',
    'billing.unavailable': 'Faturalandırma bilgileriniz şu anda yüklenemedi. Hiçbir şey değişmedi — lütfen tekrar deneyin.',
    'billing.checkout_done': 'Teşekkürler — ödemeniz alındı. Sağlayıcı onayladığı anda plan güncellenir, genellikle birkaç saniye içinde.',
    'billing.checkout_cancelled': 'Ödeme iptal edildi. Hiçbir ücret alınmadı.',
    'billing.plan': 'Plan',
    'billing.status': 'Durum',
    'billing.amount': 'Tutar',
    'billing.amount_excludes_tax': 'Vergi hariç. Stripe bulunduğunuz yerde geçerli vergiyi ekler, bu yüzden ekstrenizde ve Stripe faturasında daha yüksek bir tutar görebilirsiniz.',
    'billing.renews': 'Yenilenme',
    'billing.status_active': 'Etkin',
    'billing.status_past_due': 'Ödeme gecikmiş',
    'billing.status_unpaid': 'Ödenmedi — plan sona erdi',
    'billing.status_incomplete': 'Henüz etkin değil',
    'billing.status_canceled': 'İptal edildi',
    'billing.managed_by_stripe': 'Ödemeler ve faturalar, kayıtlı satıcı olan Stripe tarafından yürütülür.',
    'billing.see_plans': 'Planları gör',
    /* Cancelling — STORY-034. */
    'billing.ends_on': '{when} tarihinde sona erer. O zamana kadar her şey sizde kalır.',
    'billing.renews_on': '{when} tarihinde yenilenir.',
    'billing.cancel': 'Aboneliği iptal et',
    'billing.cancel_confirm': 'Bu abonelik yenilenmeyi durdursun mu?',
    'billing.cancel_confirm_body': 'Ödemesini yaptığınız dönemin sonuna kadar sürer ve hiçbir şey silinmez. O tarihe kadar istediğiniz zaman yeniden başlatabilirsiniz.',
    'billing.cancel_confirm_yes': 'Evet, yenilemeyi durdur',
    'billing.cancel_confirm_no': 'Devam etsin',
    'billing.resume': 'Aboneliği sürdür',
    'billing.cancel_requested': 'İstek gönderildi. Sağlayıcı onayladığı anda burada güncellenir, genellikle birkaç saniye içinde.',
    'billing.cancel_failed': 'Bu, ödeme sağlayıcısına iletilemedi. Hiçbir şey değişmedi — lütfen tekrar deneyin.',
    'nav.billing': 'Faturalandırma',
    'plans.free': 'Ücretsiz',
    'plans.pro': 'Pro',
    'plans.business': 'Business',
    'plans.enterprise': 'Enterprise',
    'plans.active_events': 'Etkin etkinlikler',
    'plans.seats': 'Koltuklar',
    'plans.contacts': 'Kişiler',
    'plans.unlimited': 'Sınırsız',
    'plans.usage': '{limit} içinden {used} kullanıyorsunuz',
    'plans.usage_unlimited': '{used} kullanıyorsunuz',
    'plans.current': 'Mevcut planınız',
    'plans.active_event_meaning': 'Etkin etkinlik, Planlandı ya da Onaylandı olan etkinliktir. Fikirler ücretsizdir; bir etkinliği tamamlamak ya da iptal etmek yerini serbest bırakır.',
    'plans.teams_need_pro': 'Ücretsiz plan bir koltuk içerir; başkalarıyla çalışmak için Pro veya üstü gerekir.',
    'plans.at_limit': 'Bu planda sınıra ulaştınız.',
    'plans.near_limit': 'Bu planda sınıra yaklaştınız.',
    'plans.loading': 'Planlar yükleniyor…',
    'invite.heading': 'Birini davet edin',
    'invite.email_label': 'E-posta adresi',
    'invite.role_label': 'Neler yapabilir',
    'invite.submit': 'Daveti gönder',
    'invite.sent': 'Davet yolda.',
    'invite.pending': 'Kabul edilmeyi bekleyenler',
    'invite.code_label': '{email} için kod',
    'invite.code_hint': 'E-posta ulaşmazsa bu kodu okuyun. Davetle birlikte geçerliliğini yitirir.',
    'invite.revoke': '{email} adresine gönderilen daveti geri çek',
    'invite.revoked': 'Davet geri çekildi.',
    'invite.none': 'Kabul etmeyi bekleyen kimse yok.',
    'invite.remove': '{name} kişisini bu çalışma alanından çıkar',
    'invite.remove_confirm': '{name} çıkarılsın mı?',
    'invite.remove_explains': 'Hesabı kalır ve burada yazdığı her şey hesabıyla birlikte kalır. Yalnızca bu çalışma alanına erişimini kaybeder.',
    'invite.remove_cancel': 'Kalsın',
    'requests.heading': 'Katılmak isteyenler',
    'requests.body': 'Çalışma alanı kimliğini bilen biri içeri alınmayı istedi. Bekleyen bir istek, onaylanana kadar hiçbir şey vermez.',
    'requests.asked': '{when} istendi',
    'requests.approve': '{name} kişisini onayla',
    'requests.decline': '{name} kişisini reddet',
    'requests.approve_label': 'Onayla',
    'requests.decline_label': 'Reddet',
    'requests.seat_limit': '{plan} ile onları içeri alın',
    'requests.seat_limit_limit': 'Planınızda onlara yer yok. Yer açılana kadar istek burada kalır — hiçbir şey kaybolmaz.',
    'invite.remove_yes': 'Çıkar',
    'invite.last_owner': 'Bir çalışma alanında her zaman en az bir Sahip kalır ve alan hiç boşalmaz. Önce başka birini yükseltin.',
    'invite.seat_limit_lead': '{plan} ile daha fazla kişiyle çalışın',
    'invite.seat_limit_limit': 'Bu çalışma alanı planındaki tüm koltukları kullanıyor.',
    'invite.seat_limit_alternative': 'Ya da ayrılmış birini çıkarın — boşalan koltuk hemen kullanılabilir.',
    'invite.see_plans': 'Planlara bakın',
    'accept.title': 'Ekibe katılın',
    'accept.body': 'Size verilen kodu girin veya davet e-postanızdaki bağlantıyı kullanın.',
    'accept.code_label': 'Davet kodu',
    'accept.code_hint': 'ABCD-2345 gibi sekiz karakter. Büyük veya küçük harf, tire isteğe bağlı.',
    'accept.submit': 'Katıl',
    'accept.joining': 'Davet kontrol ediliyor…',
    'accept.joined': '{name} içindesiniz.',
    'accept.invalid': 'Bu davet artık geçerli değil. Sizi davet eden kişiden yenisini isteyin.',
    'accept.wrong_account': 'Davetler gönderildikleri adrese bağlıdır. O adresle giriş yapın veya bu adrese davet isteyin.',
    'auth.forgot.title': 'Parolanızı sıfırlayın',
    'auth.forgot.body': 'Giriş yaptığınız adresi yazın, size bir bağlantı gönderelim.',
    'auth.forgot.email_label': 'E-posta adresi',
    'auth.forgot.submit': 'Bağlantıyı gönder',
    'auth.forgot.back': 'Giriş sayfasına dön',
    'auth.forgot.sent_title': 'E-postanızı kontrol edin',
    'auth.forgot.sent_body': 'Bu adrese ait bir hesap varsa, bağlantı yola çıktı. Bağlantı bir kez çalışır ve bir saat sonra geçersiz olur.',
    'auth.reset.title': 'Yeni bir parola seçin',
    'auth.reset.password_label': 'Yeni parola',
    'auth.reset.submit': 'Parolayı belirle',
    'auth.reset.done_title': 'Parola değiştirildi',
    'auth.reset.done_body': 'Diğer tüm oturumlarınız kapatıldı. Yeni parolanızla giriş yapın.',
    'auth.reset.bad_link_title': 'Bu bağlantı artık çalışmıyor',
    'auth.reset.bad_link_body': 'Sıfırlama bağlantıları bir kez çalışır ve bir saat sonra geçersiz olur. Yeni bir tane isteyin.',
    'auth.reset.request_another': 'Yeni bağlantı iste',
    'error.required': 'Bu alan boş bırakılamaz.',
    'error.email_invalid': 'Bu bir e-posta adresine benzemiyor.',
    'error.password_too_short': 'En az 8 karakter kullanın.',
    'error.rate_limited': 'Çok fazla deneme. Biraz sonra tekrar deneyin.',
    'error.network': 'Sunucuya ulaşamadık. Bağlantınızı kontrol edip tekrar deneyin.',
    'error.unexpected': 'Bir şeyler ters gitti. Tekrar deneyin.',
    'common.loading': 'Yükleniyor…',
    'common.submitting': 'Çalışıyor…',
    'email.verify.subject': 'E-posta adresinizi doğrulayın',
    'email.verify.body': 'eventuley’e hoş geldiniz.\n\nDevam etmek için e-posta adresinizi doğrulayın:\n{link}\n\nBağlantı bir kez çalışır ve 24 saat sonra geçersiz olur.\nKaydolmadıysanız bu mesajı yok sayın — bu bağlantıya dokunmadığınız sürece adınıza hiçbir şey oluşturulmaz.',
    'email.already_registered.subject': 'eventuley hesabınız zaten var',
    'email.already_registered.body': 'Biri bu adresle kaydolmayı denedi, ancak adres zaten kayıtlı.\n\nSizseniz, bunun yerine giriş yapın:\n{link}\n\nParolanızı unuttuysanız aynı sayfadan sıfırlayabilirsiniz.\nSiz değilseniz yapmanız gereken bir şey yok. Hesabınızda hiçbir şey değişmedi.',
    'email.reset.subject': 'eventuley parolanızı sıfırlayın',
    'email.reset.body': 'Bu hesabın parolasını sıfırlamak için bir istek geldi.\n\nYeni parolanızı buradan seçin:\n{link}\n\nBağlantı bir kez çalışır ve bir saat sonra geçersiz olur. Yeni bir parola belirlemek diğer tüm oturumlarınızı kapatır.\n\nSiz değilseniz bu mesajı yok sayın. Parolanız değişmedi ve bu bağlantı olmadan kimse değiştiremez.',
};
export const CATALOGUES = { en, tr };
//# sourceMappingURL=catalogue.js.map